/**
 * Elite Batch 3 Part 3 — Workflow Templates 205–226
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 3 PART 3 — TEMPLATES 205–226
// =============================================================================

export const ELITE_BATCH3_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 205. BLOCKERS AND DEPENDENCIES SLACK BOT (8 nodes)
  // ===================================================================

  "Blockers and Dependencies Slack Bot": {
    summary:
      "Monitors sprint events for blocked Jira issues, maps dependency chains, uses AI to assess downstream impact, generates resolution suggestions, posts a structured report to Slack, and logs SLA compliance for blocked items.",
    nodes: [
      trigger("Webhook: Sprint blocker event", "webhook_app", {
        source: "Jira / Linear",
        event: "issue.status.blocked",
        howItStarts:
          "A Jira or Linear issue is flagged as blocked or a dependency link is created during a sprint",
        setup:
          "In Jira -> Settings -> Webhooks, add a webhook for issue_updated events filtered to status=Blocked. Point it to the workflow URL. For Linear, use Settings -> Webhooks -> Issue updates.",
        inputFields: [],
      }),

      // 2. Pull blocked issue details from Jira
      n("2", "hitApi", "Jira: Pull blocked issue details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://your-domain.atlassian.net/rest/api/3/issue/{issue_key}?expand=changelog,names",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "fields.summary",
              customName: "issue_summary",
              type: "string",
            },
            {
              name: "fields.status.name",
              customName: "issue_status",
              type: "string",
            },
            {
              name: "fields.assignee.displayName",
              customName: "assignee_name",
              type: "string",
            },
            {
              name: "fields.priority.name",
              customName: "priority",
              type: "string",
            },
            {
              name: "fields.issuelinks",
              customName: "issue_links",
              type: "string",
            },
            {
              name: "fields.sprint.name",
              customName: "sprint_name",
              type: "string",
            },
            { name: "key", customName: "issue_key", type: "string" },
          ],
        },
      }),

      // 3. Map dependency chain
      n("3", "runCode", "Map dependency chain", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Parse issue links and build dependency chain
const links = typeof inputs.issue_links === 'string' ? JSON.parse(inputs.issue_links || '[]') : (inputs.issue_links || []);
const blockers = [];
const blocked_by = [];
const downstream = [];

for (const link of links) {
  if (link.type && link.type.inward && link.type.inward.includes('blocked by') && link.inwardIssue) {
    blocked_by.push({ key: link.inwardIssue.key, summary: link.inwardIssue.fields?.summary || 'Unknown' });
  }
  if (link.type && link.type.outward && link.type.outward.includes('blocks') && link.outwardIssue) {
    downstream.push({ key: link.outwardIssue.key, summary: link.outwardIssue.fields?.summary || 'Unknown' });
  }
}

const chain_depth = blocked_by.length + downstream.length;
const dependency_map = JSON.stringify({ blocked_by, downstream, chain_depth });
const blocked_by_keys = blocked_by.map(b => b.key).join(', ') || 'None identified';
const downstream_keys = downstream.map(d => d.key).join(', ') || 'None identified';

return { dependency_map, blocked_by_keys, downstream_keys, chain_depth: String(chain_depth), blocker_count: String(blocked_by.length) };`,
          inputVariables: [{ name: "issue_links", type: "string" }],
          outputVariables: [
            { name: "dependency_map", type: "string" },
            { name: "blocked_by_keys", type: "string" },
            { name: "downstream_keys", type: "string" },
            { name: "chain_depth", type: "string" },
            { name: "blocker_count", type: "string" },
          ],
        },
      }),

      // 4. AI impact analysis
      aiNode(
        "4",
        "AI assess downstream impact",
        760,
        "You are a sprint management analyst. Given a blocked issue, its dependency chain, and sprint context, assess the downstream impact on sprint delivery. Estimate risk level (critical/high/medium/low), days of delay if unresolved, and affected team members. Respond JSON: {risk_level: string, estimated_delay_days: number, affected_teams: string[], sprint_impact_summary: string, cascade_risk: string}",
        "Blocked issue: {issue_key} - {issue_summary}. Priority: {priority}. Assignee: {assignee_name}. Sprint: {sprint_name}. Blocked by: {blocked_by_keys}. Downstream: {downstream_keys}. Chain depth: {chain_depth}. Dependency map: {dependency_map}",
        [
          { field: "risk_level", as: "risk_level", type: "string" },
          {
            field: "estimated_delay_days",
            as: "estimated_delay_days",
            type: "number",
          },
          { field: "affected_teams", as: "affected_teams", type: "string" },
          {
            field: "sprint_impact_summary",
            as: "sprint_impact_summary",
            type: "string",
          },
          { field: "cascade_risk", as: "cascade_risk", type: "string" },
        ],
      ),

      // 5. AI resolution suggestions
      aiNode(
        "5",
        "AI generate resolution suggestions",
        1000,
        "You are an engineering team lead. Given a blocked issue with its dependency chain and impact analysis, suggest concrete resolution actions. Include who should act, what to do, and fallback options. Respond JSON: {primary_action: string, owner: string, fallback_options: string[], escalation_needed: string, suggested_workaround: string, unblock_steps: string[]}",
        "Issue: {issue_key} - {issue_summary}. Risk: {risk_level}. Delay: {estimated_delay_days} days. Blocked by: {blocked_by_keys}. Cascade risk: {cascade_risk}. Sprint impact: {sprint_impact_summary}. Assignee: {assignee_name}",
        [
          { field: "primary_action", as: "primary_action", type: "string" },
          { field: "owner", as: "resolution_owner", type: "string" },
          { field: "fallback_options", as: "fallback_options", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
          {
            field: "suggested_workaround",
            as: "suggested_workaround",
            type: "string",
          },
          { field: "unblock_steps", as: "unblock_steps", type: "string" },
        ],
      ),

      // 6. Build Slack report
      n("6", "runCode", "Build structured Slack report", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Build formatted Slack report
const riskEmoji = { critical: '🔴', high: '🟠', medium: '🟡', low: '🟢' };
const emoji = riskEmoji[inputs.risk_level] || '⚪';
const report = emoji + ' *Sprint Blocker Alert*\\n' +
  '*Issue:* ' + inputs.issue_key + ' — ' + inputs.issue_summary + '\\n' +
  '*Risk Level:* ' + inputs.risk_level.toUpperCase() + ' | *Est. Delay:* ' + inputs.estimated_delay_days + ' days\\n' +
  '*Assignee:* ' + inputs.assignee_name + ' | *Sprint:* ' + inputs.sprint_name + '\\n' +
  '---\\n' +
  '*Blocked By:* ' + inputs.blocked_by_keys + '\\n' +
  '*Downstream Impact:* ' + inputs.downstream_keys + '\\n' +
  '*Cascade Risk:* ' + inputs.cascade_risk + '\\n' +
  '---\\n' +
  '*Resolution:* ' + inputs.primary_action + '\\n' +
  '*Owner:* ' + inputs.resolution_owner + '\\n' +
  '*Workaround:* ' + inputs.suggested_workaround + '\\n' +
  '*Escalation Needed:* ' + inputs.escalation_needed;

return { slack_report: report, report_generated: 'true' };`,
          inputVariables: [
            { name: "risk_level", type: "string" },
            { name: "estimated_delay_days", type: "number" },
            { name: "issue_key", type: "string" },
            { name: "issue_summary", type: "string" },
            { name: "assignee_name", type: "string" },
            { name: "sprint_name", type: "string" },
            { name: "blocked_by_keys", type: "string" },
            { name: "downstream_keys", type: "string" },
            { name: "cascade_risk", type: "string" },
            { name: "primary_action", type: "string" },
            { name: "resolution_owner", type: "string" },
            { name: "suggested_workaround", type: "string" },
            { name: "escalation_needed", type: "string" },
          ],
          outputVariables: [
            { name: "slack_report", type: "string" },
            { name: "report_generated", type: "string" },
          ],
        },
      }),

      // 7. Post to Slack
      slackNode("7", "Post blocker report to Slack", 1480, "{slack_report}"),

      // 8. Log SLA compliance
      n("8", "hitApi", "Log blocker SLA to Google Sheets", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/BlockerSLA!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{issue_key}","{issue_summary}","{risk_level}","{estimated_delay_days}","{blocked_by_keys}","{primary_action}","{resolution_owner}","{sprint_name}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "sla_rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 206. COMMENT TRIAGE SYSTEM (8 nodes)
  // ===================================================================

  "Comment Triage System": {
    summary:
      "Ingests comments from any platform, classifies intent and sentiment via AI, routes to the appropriate team based on classification, creates support tickets or CRM records, drafts contextual replies, and logs triage outcomes for analytics.",
    nodes: [
      trigger("Webhook: New comment received", "webhook_app", {
        source: "Social / Blog / App Reviews",
        event: "comment.created",
        howItStarts:
          "A new comment arrives from any connected platform (social media, blog, app store, support forum)",
        setup:
          "Connect comment sources (Facebook, Instagram, blog CMS, app store review webhooks) to POST comment data to the workflow URL. Include the comment text, author info, and source platform.",
        inputFields: [],
      }),

      // 2. AI classify comment intent
      aiNode(
        "2",
        "AI classify comment intent",
        280,
        "You are a comment triage specialist. Read this incoming comment payload and classify it. Find the comment text, author, source platform, and any context regardless of field naming. Classify intent as: support_request, bug_report, feature_request, praise, complaint, spam, question, or general. Respond JSON: {comment_text: string, author_name: string, author_email: string, source_platform: string, intent: string, urgency: string, product_area: string}",
        "{webhook_payload}",
        [
          { field: "comment_text", as: "comment_text", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "source_platform", as: "source_platform", type: "string" },
          { field: "intent", as: "comment_intent", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "product_area", as: "product_area", type: "string" },
        ],
      ),

      // 3. AI sentiment scoring
      aiNode(
        "3",
        "AI sentiment scoring",
        520,
        "You are a sentiment analysis expert. Score the sentiment of this comment on a scale from -1.0 (very negative) to 1.0 (very positive). Detect emotion (angry, frustrated, neutral, happy, excited). Flag toxic language. Respond JSON: {sentiment_score: number, emotion: string, is_toxic: string, tone_summary: string, escalation_recommended: string}",
        "Comment: {comment_text}. Intent: {comment_intent}. Platform: {source_platform}",
        [
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "emotion", as: "emotion", type: "string" },
          { field: "is_toxic", as: "is_toxic", type: "string" },
          { field: "tone_summary", as: "tone_summary", type: "string" },
          {
            field: "escalation_recommended",
            as: "escalation_recommended",
            type: "string",
          },
        ],
      ),

      // 4. Route by intent
      n("4", "condition", "Route: support request or complaint?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "comment_intent",
              operator: "in",
              value: "support_request,bug_report,complaint",
            },
          ],
        },
      }),

      // 5a. True: Create Zendesk ticket for support
      n("5", "hitApi", "Create Zendesk support ticket", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"subject":"[{source_platform}] {comment_intent}: {product_area}","comment":{"body":"{comment_text}\\n\\nAuthor: {author_name}\\nSentiment: {sentiment_score} ({emotion})\\nUrgency: {urgency}"},"priority":"{urgency}","requester":{"name":"{author_name}","email":"{author_email}"}}',
            },
          ],
          variables: [
            { name: "ticket.id", customName: "ticket_id", type: "string" },
            { name: "ticket.url", customName: "ticket_url", type: "string" },
          ],
        },
      }),

      // 5b. False: Log to HubSpot as feedback
      n(
        "5b",
        "hitApi",
        "Log feedback to HubSpot",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.hubapi.com/crm/v3/objects/notes",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"hs_note_body":"[{source_platform}] {comment_intent}: {comment_text}\\nSentiment: {sentiment_score} ({emotion})\\nProduct area: {product_area}","hs_timestamp":"{timestamp}"}',
              },
            ],
            variables: [
              { name: "id", customName: "hubspot_note_id", type: "string" },
            ],
          },
        },
        500,
      ),

      // 6. AI draft reply
      aiNode(
        "6",
        "AI draft contextual reply",
        1240,
        "You are a customer communications specialist. Draft a reply to this comment that matches the tone of the platform, acknowledges the users concern, and provides a helpful response. Be empathetic for negative sentiment, grateful for positive. Keep it concise and professional. Respond JSON: {reply_text: string, reply_tone: string, includes_action_item: string}",
        "Comment: {comment_text}. Intent: {comment_intent}. Sentiment: {sentiment_score} ({emotion}). Platform: {source_platform}. Product area: {product_area}. Escalation recommended: {escalation_recommended}",
        [
          { field: "reply_text", as: "reply_text", type: "string" },
          { field: "reply_tone", as: "reply_tone", type: "string" },
          {
            field: "includes_action_item",
            as: "includes_action_item",
            type: "string",
          },
        ],
      ),

      // 7. Slack triage notification
      slackNode(
        "7",
        "Slack triage notification",
        1480,
        "💬 *Comment Triage*\n*Platform:* {source_platform} | *Intent:* {comment_intent}\n*Author:* {author_name} | *Sentiment:* {sentiment_score} ({emotion})\n*Urgency:* {urgency} | *Toxic:* {is_toxic}\n*Comment:* {comment_text}\n*Drafted Reply:* {reply_text}\n*Escalation:* {escalation_recommended}",
      ),

      // 8. Log to analytics
      n("8", "hitApi", "Log triage outcome to Sheets", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/CommentTriage!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{source_platform}","{comment_intent}","{sentiment_score}","{emotion}","{urgency}","{author_name}","{product_area}","{escalation_recommended}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "triage_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 207. COMMUNITY ANSWER DRAFTER (7 nodes)
  // ===================================================================

  "Community Answer Drafter": {
    summary:
      "Scans community forums for unanswered questions, pulls contextual information, retrieves relevant knowledge base articles from Pinecone, drafts AI answers, gates low-quality answers for human review, and posts approved answers with logging.",
    nodes: [
      trigger("Schedule: Scan for unanswered questions", "schedule", {
        schedule: "Every 30 minutes",
        howItStarts:
          "Runs on a 30-minute schedule to scan community forums for unanswered questions older than a configurable threshold",
        setup:
          "Configure your community platform API credentials (Discourse, Circle, or custom forum). Set the unanswered threshold (e.g., 2 hours). The workflow fetches questions with zero replies.",
        inputFields: [],
      }),

      // 2. Fetch unanswered questions
      n("2", "hitApi", "Fetch unanswered community questions", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://community.example.com/api/topics.json?status=unsolved&min_age=7200&per_page=10&api_key={community_api_key}",
          headers: [
            { key: "Api-Username", value: "system" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "topic_list.topics[0].id",
              customName: "question_id",
              type: "string",
            },
            {
              name: "topic_list.topics[0].title",
              customName: "question_title",
              type: "string",
            },
            {
              name: "topic_list.topics[0].excerpt",
              customName: "question_body",
              type: "string",
            },
            {
              name: "topic_list.topics[0].category_id",
              customName: "category_id",
              type: "string",
            },
            {
              name: "topic_list.topics[0].created_at",
              customName: "question_created_at",
              type: "string",
            },
            {
              name: "topic_list.topics[0].posters[0].user_id",
              customName: "author_id",
              type: "string",
            },
          ],
        },
      }),

      // 3. KB retrieval via Pinecone
      n("3", "hitApi", "Pinecone: Retrieve relevant KB articles", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_environment}.pinecone.io/query",
          headers: [
            { key: "Api-Key", value: "{pinecone_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "namespace", value: "knowledge_base" },
            { key: "topK", value: "5" },
            { key: "includeMetadata", value: "true" },
            { key: "vector", value: "{question_embedding}" },
          ],
          variables: [
            { name: "matches", customName: "kb_matches", type: "string" },
            {
              name: "matches[0].metadata.content",
              customName: "top_kb_content",
              type: "string",
            },
            {
              name: "matches[0].score",
              customName: "top_match_score",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI draft answer
      aiNode(
        "4",
        "AI draft community answer",
        760,
        "You are a helpful community expert. Given a community question and relevant knowledge base articles, draft a clear, accurate, and friendly answer. Cite specific KB articles when relevant. If the KB content does not sufficiently answer the question, say so honestly and suggest next steps. Rate your confidence. Respond JSON: {answer_text: string, confidence: number, sources_cited: string[], needs_human_review: string, answer_quality: string}",
        "Question title: {question_title}. Question body: {question_body}. Top KB match (score {top_match_score}): {top_kb_content}. All matches: {kb_matches}",
        [
          { field: "answer_text", as: "answer_text", type: "string" },
          { field: "confidence", as: "answer_confidence", type: "number" },
          { field: "sources_cited", as: "sources_cited", type: "string" },
          {
            field: "needs_human_review",
            as: "needs_human_review",
            type: "string",
          },
          { field: "answer_quality", as: "answer_quality", type: "string" },
        ],
      ),

      // 5. Quality gate condition
      n("5", "condition", "Quality check: confidence above threshold?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "needs_human_review", operator: "equals", value: "false" },
          ],
        },
      }),

      // 6a. True: High quality — approval gate then post
      n("6", "approvalGate", "Final review before posting", 1240, {
        approvalGateData: {
          message:
            "Community answer for: {question_title}\n\nDrafted answer: {answer_text}\n\nConfidence: {answer_confidence} | Quality: {answer_quality}\nSources: {sources_cited}",
        },
      }),

      // 6b. False: Low quality — route to human queue with Slack alert
      slackNode(
        "6b",
        "Slack: Flag for human answering",
        1240,
        "⚠️ *Low-Confidence Community Answer*\n*Question:* {question_title}\n*Draft:* {answer_text}\n*Confidence:* {answer_confidence}\n*Quality:* {answer_quality}\nThis answer needs a human expert. Question ID: {question_id}",
        500,
      ),

      // 7. Post answer + log
      n("7", "hitApi", "Post answer to community + log", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://community.example.com/api/posts.json",
          headers: [
            { key: "Api-Key", value: "{community_api_key}" },
            { key: "Api-Username", value: "omniengage-bot" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "topic_id", value: "{question_id}" },
            {
              key: "raw",
              value: "{answer_text}\n\n---\n*Sources:* {sources_cited}",
            },
          ],
          variables: [
            { name: "id", customName: "post_id", type: "string" },
            {
              name: "topic_id",
              customName: "answered_topic_id",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6b", "output_2"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 208. COMMUNITY MODERATION ALERT (7 nodes)
  // ===================================================================

  "Community Moderation Alert": {
    summary:
      "Triggers on new community posts, runs AI moderation scanning for policy violations, quarantines risky content, alerts moderators via Slack, logs all moderation decisions, and tracks appeals for overturned actions.",
    nodes: [
      trigger("Webhook: New community post", "webhook_app", {
        source: "Community Platform / Forum",
        event: "post.created",
        howItStarts:
          "A new post or comment is submitted to the community platform",
        setup:
          "Connect your community platform (Discourse, Circle, custom forum) to POST new post data to the workflow URL. Include post content, author info, and category.",
        inputFields: [],
      }),

      // 2. AI moderation scan
      aiNode(
        "2",
        "AI moderation scan",
        280,
        "You are a community content moderator. Analyze this post for policy violations including: hate speech, harassment, spam, self-promotion, misinformation, NSFW content, personal attacks, and off-topic content. Find the post text, author, and category regardless of field naming. Be thorough but fair. Respond JSON: {post_text: string, author_name: string, author_id: string, category: string, violations_found: string[], risk_score: number, primary_violation: string, explanation: string, recommended_action: string, false_positive_risk: string}",
        "{webhook_payload}",
        [
          { field: "post_text", as: "post_text", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_id", as: "author_id", type: "string" },
          { field: "category", as: "post_category", type: "string" },
          { field: "violations_found", as: "violations_found", type: "string" },
          { field: "risk_score", as: "risk_score", type: "number" },
          {
            field: "primary_violation",
            as: "primary_violation",
            type: "string",
          },
          {
            field: "explanation",
            as: "moderation_explanation",
            type: "string",
          },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "false_positive_risk",
            as: "false_positive_risk",
            type: "string",
          },
        ],
      ),

      // 3. Risk condition
      n("3", "condition", "Risk score above quarantine threshold?", 520, {
        conditionDetails: {
          conditions: [
            { field: "risk_score", operator: "greaterThan", value: "0.7" },
          ],
        },
      }),

      // 4. True: Auto-quarantine via API
      n("4", "hitApi", "Auto-quarantine risky post", 760, {
        hitApiDetails: {
          method: "PUT",
          url: "https://community.example.com/api/posts/{post_id}/status.json",
          headers: [
            { key: "Api-Key", value: "{community_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "status", value: "quarantined" },
            {
              key: "reason",
              value: "{primary_violation}: {moderation_explanation}",
            },
          ],
          variables: [
            { name: "status", customName: "quarantine_status", type: "string" },
          ],
        },
      }),

      // 5. Moderator Slack alert
      slackNode(
        "5",
        "Slack: Moderator alert",
        1000,
        "🛡️ *Community Moderation Alert*\n*Risk Score:* {risk_score} | *Action:* {recommended_action}\n*Violation:* {primary_violation}\n*Author:* {author_name} (ID: {author_id})\n*Category:* {post_category}\n*Post:* {post_text}\n*Explanation:* {moderation_explanation}\n*False Positive Risk:* {false_positive_risk}",
      ),

      // 6. Policy logging
      n("6", "hitApi", "Log moderation decision to Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ModerationLog!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{author_id}","{author_name}","{risk_score}","{primary_violation}","{recommended_action}","{moderation_explanation}","{false_positive_risk}","{post_category}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "moderation_logged",
              type: "number",
            },
          ],
        },
      }),

      // 7. Appeals tracking placeholder
      n("7", "hitApi", "Initialize appeals tracking record", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Appeals!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{author_id}","{primary_violation}","{risk_score}","pending","",""]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "appeal_record_created",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 209. CROSS PLATFORM CHAT UNIFIER (8 nodes)
  // ===================================================================

  "Cross Platform Chat Unifier": {
    summary:
      "Ingests messages from multiple chat platforms, normalizes them into a common schema, uses AI to detect intent and urgency, routes messages by type, reformats for the target channel, forwards to the right destination, monitors SLA compliance, and logs all cross-platform activity.",
    nodes: [
      trigger("Webhook: Multi-platform chat message", "webhook_app", {
        source: "Slack / Discord / Teams / Intercom",
        event: "message.received",
        howItStarts:
          "A chat message arrives from any connected platform (Slack, Discord, Microsoft Teams, Intercom, or custom chat)",
        setup:
          "Connect each chat platform webhook to POST messages to this workflow URL. Slack: Event Subscriptions -> message.channels. Discord: Bot -> MESSAGE_CREATE. Teams: Bot Framework webhook. Intercom: Webhooks -> conversation.user.created.",
        inputFields: [],
      }),

      // 2. Normalize message format
      n("2", "runCode", "Normalize message schema", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Normalize messages from different platforms into a common schema
const raw = typeof inputs.webhook_payload === 'string' ? JSON.parse(inputs.webhook_payload) : inputs.webhook_payload;
let normalized = { text: '', author: '', platform: 'unknown', channel: '', timestamp: '', thread_id: '', has_attachments: 'false' };

// Slack format
if (raw.event && raw.event.text) {
  normalized = { text: raw.event.text, author: raw.event.user || '', platform: 'slack', channel: raw.event.channel || '', timestamp: raw.event.ts || '', thread_id: raw.event.thread_ts || '', has_attachments: (raw.event.files && raw.event.files.length > 0) ? 'true' : 'false' };
}
// Discord format
else if (raw.content !== undefined) {
  normalized = { text: raw.content, author: raw.author?.username || '', platform: 'discord', channel: raw.channel_id || '', timestamp: raw.timestamp || '', thread_id: raw.message_reference?.message_id || '', has_attachments: (raw.attachments && raw.attachments.length > 0) ? 'true' : 'false' };
}
// Teams format
else if (raw.text && raw.from) {
  normalized = { text: raw.text, author: raw.from?.name || '', platform: 'teams', channel: raw.channelId || '', timestamp: raw.timestamp || '', thread_id: raw.replyToId || '', has_attachments: (raw.attachments && raw.attachments.length > 0) ? 'true' : 'false' };
}
// Intercom format
else if (raw.data && raw.data.item) {
  const item = raw.data.item;
  normalized = { text: item.conversation_message?.body || '', author: item.conversation_message?.author?.name || '', platform: 'intercom', channel: item.id || '', timestamp: item.created_at || '', thread_id: item.id || '', has_attachments: 'false' };
}

return normalized;`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "text", type: "string" },
            { name: "author", type: "string" },
            { name: "platform", type: "string" },
            { name: "channel", type: "string" },
            { name: "timestamp", type: "string" },
            { name: "thread_id", type: "string" },
            { name: "has_attachments", type: "string" },
          ],
        },
      }),

      // 3. AI intent detection
      aiNode(
        "3",
        "AI detect intent and urgency",
        520,
        "You are a message triage specialist. Classify this chat message by intent: question, request, bug_report, feedback, escalation, social, or info_share. Also rate urgency (critical/high/medium/low) and determine the best team to handle it (support, engineering, sales, ops, general). Respond JSON: {intent: string, urgency: string, target_team: string, summary: string, requires_response: string, language: string}",
        "Message from {author} on {platform} (channel: {channel}): {text}",
        [
          { field: "intent", as: "message_intent", type: "string" },
          { field: "urgency", as: "message_urgency", type: "string" },
          { field: "target_team", as: "target_team", type: "string" },
          { field: "summary", as: "message_summary", type: "string" },
          {
            field: "requires_response",
            as: "requires_response",
            type: "string",
          },
          { field: "language", as: "detected_language", type: "string" },
        ],
      ),

      // 4. Route by target team
      n("4", "condition", "Route: engineering or support?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "target_team",
              operator: "in",
              value: "engineering,support",
            },
          ],
        },
      }),

      // 5a. True: Format for support/eng channel
      n("5", "runCode", "Format relay message for target channel", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Format the message for the target channel with context
const prefix = inputs.message_urgency === 'critical' ? '🔴 CRITICAL' : inputs.message_urgency === 'high' ? '🟠 HIGH' : '📨';
const formatted = prefix + ' | From ' + inputs.platform + ' #' + inputs.channel + '\\n' +
  '*Author:* ' + inputs.author + ' | *Intent:* ' + inputs.message_intent + '\\n' +
  '*Summary:* ' + inputs.message_summary + '\\n' +
  '---\\n' + inputs.text;
const target_channel = inputs.target_team === 'engineering' ? '#eng-triage' : inputs.target_team === 'support' ? '#support-queue' : '#general-triage';
return { formatted_message: formatted, target_slack_channel: target_channel };`,
          inputVariables: [
            { name: "message_urgency", type: "string" },
            { name: "platform", type: "string" },
            { name: "channel", type: "string" },
            { name: "author", type: "string" },
            { name: "message_intent", type: "string" },
            { name: "message_summary", type: "string" },
            { name: "text", type: "string" },
            { name: "target_team", type: "string" },
          ],
          outputVariables: [
            { name: "formatted_message", type: "string" },
            { name: "target_slack_channel", type: "string" },
          ],
        },
      }),

      // 5b. False: General channel forward
      slackNode(
        "5b",
        "Forward to general triage channel",
        1000,
        "📨 *Cross-Platform Message*\nFrom {author} on {platform}: {text}\nIntent: {message_intent} | Team: {target_team}",
        500,
      ),

      // 6. Forward to target channel
      slackNode(
        "6",
        "Forward to target Slack channel",
        1240,
        "{formatted_message}",
      ),

      // 7. SLA monitor
      n("7", "hitApi", "Log cross-platform activity + SLA", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/ChatUnifier!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{platform}","{author}","{message_intent}","{message_urgency}","{target_team}","{requires_response}","{timestamp}","{target_slack_channel}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "activity_logged",
              type: "number",
            },
          ],
        },
      }),

      // 8. Logging complete
      n("8", "hitApi", "SLA breach check + metrics update", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.segment.io/v1/track",
          headers: [
            { key: "Authorization", value: "Basic {segment_write_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "event", value: "cross_platform_message_routed" },
            {
              key: "properties",
              value:
                '{"platform":"{platform}","intent":"{message_intent}","urgency":"{message_urgency}","target_team":"{target_team}","requires_response":"{requires_response}"}',
            },
          ],
          variables: [
            {
              name: "success",
              customName: "sla_tracking_sent",
              type: "boolean",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 210. CROSS PLATFORM NOTIFICATION DEDUPLICATOR (7 nodes)
  // ===================================================================

  "Cross Platform Notification Deduplicator": {
    summary:
      "Ingests notifications from multiple platforms, generates content hashes for deduplication, checks a cache for existing duplicates, suppresses redundant notifications, delivers only unique consolidated notifications, updates the dedup cache, and reports reduction metrics.",
    nodes: [
      trigger("Webhook: Incoming notification", "webhook_app", {
        source: "Slack / Email / Push / SMS",
        event: "notification.received",
        howItStarts:
          "A notification is about to be sent from any platform (email, Slack, push, SMS) and passes through this deduplication gateway",
        setup:
          "Route all outbound notifications through this workflow before delivery. Each notification platform should POST the notification payload (recipient, channel, content, type) to the workflow URL for dedup checking.",
        inputFields: [],
      }),

      // 2. Hash generation
      n("2", "runCode", "Generate content hash for dedup", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Generate a dedup hash from notification content
const raw = typeof inputs.webhook_payload === 'string' ? JSON.parse(inputs.webhook_payload) : inputs.webhook_payload;
const recipient = raw.recipient || raw.to || raw.user_id || raw.email || 'unknown';
const channel = raw.channel || raw.platform || raw.type || 'unknown';
const content = raw.content || raw.message || raw.text || raw.body || '';
const subject = raw.subject || raw.title || '';

// Create a hash from recipient + normalized content (ignore timestamps, minor variations)
const normalized = (recipient + '|' + content.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 200)).trim();
let hash = 0;
for (let i = 0; i < normalized.length; i++) {
  const char = normalized.charCodeAt(i);
  hash = ((hash << 5) - hash) + char;
  hash = hash & hash; // Convert to 32bit integer
}
const content_hash = 'dedup_' + Math.abs(hash).toString(36);
const dedup_window = '3600'; // 1 hour window in seconds

return { content_hash, recipient, channel, content, subject, dedup_window, notification_type: channel };`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "content_hash", type: "string" },
            { name: "recipient", type: "string" },
            { name: "channel", type: "string" },
            { name: "content", type: "string" },
            { name: "subject", type: "string" },
            { name: "dedup_window", type: "string" },
            { name: "notification_type", type: "string" },
          ],
        },
      }),

      // 3. Cache check condition — check Redis/KV for existing hash
      n("3", "condition", "Hash found in dedup cache?", 520, {
        conditionDetails: {
          conditions: [
            { field: "cache_hit", operator: "equals", value: "true" },
          ],
        },
      }),

      // 4a. True: Suppress duplicate — log suppression
      n("4", "hitApi", "Log suppressed duplicate notification", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Suppressed!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{content_hash}","{recipient}","{channel}","suppressed","{subject}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "suppression_logged",
              type: "number",
            },
          ],
        },
      }),

      // 4b. False: Deliver consolidated notification
      slackNode(
        "4b",
        "Deliver unique notification",
        760,
        "📬 *Notification Delivered*\n*To:* {recipient} via {channel}\n*Subject:* {subject}\n*Content:* {content}\n*Hash:* {content_hash}",
        500,
      ),

      // 5. Update dedup cache
      n("5", "hitApi", "Update dedup cache with hash", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.upstash.com/v2/pipeline",
          headers: [
            { key: "Authorization", value: "Bearer {upstash_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "commands",
              value: '[["SET","{content_hash}","1","EX","{dedup_window}"]]',
            },
          ],
          variables: [
            {
              name: "results[0]",
              customName: "cache_set_result",
              type: "string",
            },
          ],
        },
      }),

      // 6. Reduction reporting
      n("6", "hitApi", "Log dedup metrics to Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/DedupMetrics!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{content_hash}","{recipient}","{notification_type}","delivered","{dedup_window}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "metrics_logged",
              type: "number",
            },
          ],
        },
      }),

      // 7. Summary report
      slackNode(
        "7",
        "Dedup reduction summary",
        1480,
        "📊 *Notification Dedup Summary*\nHash: {content_hash} | Channel: {notification_type}\nRecipient: {recipient}\nStatus: Processed | Cache updated for {dedup_window}s window",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "4b", "output_2"),
      e("4b", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 211. DISCORD MODERATION BOT (8 nodes)
  // ===================================================================

  "Discord Moderation Bot": {
    summary:
      "Ingests Discord messages via webhook, runs AI content scanning with context from user history, applies moderation actions based on severity, auto-deletes or warns violators, escalates bans through an approval gate, alerts moderators in Slack, and maintains a comprehensive audit log.",
    nodes: [
      trigger("Webhook: Discord message received", "webhook_app", {
        source: "Discord Bot",
        event: "MESSAGE_CREATE",
        howItStarts:
          "A new message is posted in a monitored Discord server channel",
        setup:
          "Set up a Discord bot with MESSAGE_CONTENT intent. Configure the bot to POST every message to this workflow URL. Include message content, author info, channel, and guild ID.",
        inputFields: [],
      }),

      // 2. AI content scan
      aiNode(
        "2",
        "AI scan message content",
        280,
        "You are a Discord server moderation AI. Analyze this message for rule violations: hate speech, harassment, spam, NSFW, self-promotion, scam links, raid activity, and personal attacks. Find the message content, author info, and channel regardless of payload format. Respond JSON: {message_content: string, author_username: string, author_id: string, channel_id: string, guild_id: string, violations: string[], severity: string, action: string, explanation: string, is_spam_pattern: string, scam_links_detected: string}",
        "{webhook_payload}",
        [
          { field: "message_content", as: "message_content", type: "string" },
          { field: "author_username", as: "author_username", type: "string" },
          { field: "author_id", as: "author_id", type: "string" },
          { field: "channel_id", as: "channel_id", type: "string" },
          { field: "guild_id", as: "guild_id", type: "string" },
          { field: "violations", as: "violations", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          { field: "action", as: "recommended_action", type: "string" },
          { field: "explanation", as: "mod_explanation", type: "string" },
          { field: "is_spam_pattern", as: "is_spam_pattern", type: "string" },
          {
            field: "scam_links_detected",
            as: "scam_links_detected",
            type: "string",
          },
        ],
      ),

      // 3. Fetch user history
      n("3", "hitApi", "Discord: Fetch user moderation history", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/DiscordMod!A:H?key={google_api_key}",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "user_history_raw", type: "string" },
          ],
        },
      }),

      // 4. Action condition: severity warrants action?
      n("4", "condition", "Severity warrants auto-action?", 760, {
        conditionDetails: {
          conditions: [
            { field: "severity", operator: "in", value: "high,critical" },
          ],
        },
      }),

      // 5a. True: Auto-delete + warn via Discord API
      n("5", "hitApi", "Discord: Delete message + warn user", 1000, {
        hitApiDetails: {
          method: "DELETE",
          url: "https://discord.com/api/v10/channels/{channel_id}/messages/{message_id}",
          headers: [{ key: "Authorization", value: "Bot {discord_bot_token}" }],
          body: [],
          variables: [
            { name: "status", customName: "delete_status", type: "string" },
          ],
        },
      }),

      // 5b. False: Log warning only
      n(
        "5b",
        "hitApi",
        "Discord: Send DM warning to user",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://discord.com/api/v10/users/@me/channels",
            headers: [
              { key: "Authorization", value: "Bot {discord_bot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [{ key: "recipient_id", value: "{author_id}" }],
            variables: [
              { name: "id", customName: "dm_channel_id", type: "string" },
            ],
          },
        },
        500,
      ),

      // 6. Ban approval gate for critical violations
      n(
        "6",
        "approvalGate",
        "Approve ban for repeat/critical offender?",
        1240,
        {
          approvalGateData: {
            message:
              "Ban request for Discord user: {author_username} (ID: {author_id})\n\nViolations: {violations}\nSeverity: {severity}\nExplanation: {mod_explanation}\nSpam pattern: {is_spam_pattern}\nScam links: {scam_links_detected}\n\nApprove to issue ban.",
          },
        },
      ),

      // 7. Moderator Slack notification
      slackNode(
        "7",
        "Slack: Moderation action notification",
        1480,
        "🛡️ *Discord Moderation Action*\n*User:* {author_username} (ID: {author_id})\n*Severity:* {severity} | *Action:* {recommended_action}\n*Violations:* {violations}\n*Message:* {message_content}\n*Explanation:* {mod_explanation}\n*Spam:* {is_spam_pattern} | *Scam links:* {scam_links_detected}",
      ),

      // 8. Audit logging
      n("8", "hitApi", "Log moderation to audit sheet", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/DiscordMod!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{author_id}","{author_username}","{severity}","{violations}","{recommended_action}","{mod_explanation}","{channel_id}","{guild_id}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "audit_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 212. DISCORD SERVER WELCOME BOT (7 nodes)
  // ===================================================================

  "Discord Server Welcome Bot": {
    summary:
      "Triggers when a new member joins the Discord server, pulls member context, generates a personalized AI welcome message, sends a DM and public greeting, assigns roles based on join source, and tracks engagement metrics.",
    nodes: [
      trigger("Webhook: Discord member join", "webhook_app", {
        source: "Discord Bot",
        event: "GUILD_MEMBER_ADD",
        howItStarts: "A new member joins the Discord server",
        setup:
          "Configure your Discord bot with GUILD_MEMBERS intent. Set up the bot to POST GUILD_MEMBER_ADD events to this workflow URL. Include the member user object and guild ID.",
        inputFields: [],
      }),

      // 2. Pull member context
      aiNode(
        "2",
        "AI extract member context",
        280,
        "You are a Discord community manager assistant. Extract the new member details from this webhook payload regardless of field naming. Look for username, discriminator, avatar, join source (invite code if available), and guild ID. Respond JSON: {username: string, user_id: string, discriminator: string, avatar_url: string, guild_id: string, join_source: string, account_age_days: number}",
        "{webhook_payload}",
        [
          { field: "username", as: "username", type: "string" },
          { field: "user_id", as: "user_id", type: "string" },
          { field: "discriminator", as: "discriminator", type: "string" },
          { field: "avatar_url", as: "avatar_url", type: "string" },
          { field: "guild_id", as: "guild_id", type: "string" },
          { field: "join_source", as: "join_source", type: "string" },
          { field: "account_age_days", as: "account_age_days", type: "number" },
        ],
      ),

      // 3. AI generate welcome message
      aiNode(
        "3",
        "AI generate personalized welcome",
        520,
        "You are a warm and enthusiastic community manager for a Discord server. Create a personalized welcome message for this new member. Reference the server purpose, suggest starter channels, and make them feel at home. Create both a DM version (detailed with tips) and a public channel version (brief and celebratory). Respond JSON: {dm_welcome: string, public_welcome: string, suggested_channels: string[], starter_role: string}",
        "New member: {username}. Join source: {join_source}. Account age: {account_age_days} days.",
        [
          { field: "dm_welcome", as: "dm_welcome", type: "string" },
          { field: "public_welcome", as: "public_welcome", type: "string" },
          {
            field: "suggested_channels",
            as: "suggested_channels",
            type: "string",
          },
          { field: "starter_role", as: "starter_role", type: "string" },
        ],
      ),

      // 4. Send DM welcome
      n("4", "hitApi", "Discord: Send DM welcome", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://discord.com/api/v10/users/@me/channels",
          headers: [
            { key: "Authorization", value: "Bot {discord_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "recipient_id", value: "{user_id}" }],
          variables: [
            { name: "id", customName: "dm_channel_id", type: "string" },
          ],
        },
      }),

      // 5. Post public welcome
      n("5", "hitApi", "Discord: Post public welcome", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://discord.com/api/v10/channels/{welcome_channel_id}/messages",
          headers: [
            { key: "Authorization", value: "Bot {discord_bot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "content", value: "{public_welcome}" }],
          variables: [
            { name: "id", customName: "welcome_message_id", type: "string" },
          ],
        },
      }),

      // 6. Assign roles
      n("6", "runCode", "Determine and assign starter role", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Determine the role ID based on join source and AI suggestion
const roleMap = {
  'community': 'COMMUNITY_ROLE_ID',
  'developer': 'DEVELOPER_ROLE_ID',
  'customer': 'CUSTOMER_ROLE_ID',
  'general': 'NEWCOMER_ROLE_ID'
};
const roleName = inputs.starter_role || 'general';
const role_id = roleMap[roleName.toLowerCase()] || roleMap['general'];
const assign_url = 'https://discord.com/api/v10/guilds/' + inputs.guild_id + '/members/' + inputs.user_id + '/roles/' + role_id;

return { role_id, assign_url, role_name: roleName, assignment_ready: 'true' };`,
          inputVariables: [
            { name: "starter_role", type: "string" },
            { name: "guild_id", type: "string" },
            { name: "user_id", type: "string" },
          ],
          outputVariables: [
            { name: "role_id", type: "string" },
            { name: "assign_url", type: "string" },
            { name: "role_name", type: "string" },
            { name: "assignment_ready", type: "string" },
          ],
        },
      }),

      // 7. Track engagement
      n("7", "hitApi", "Log welcome engagement to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/DiscordWelcome!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{user_id}","{username}","{join_source}","{role_name}","{account_age_days}","{suggested_channels}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "welcome_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 213. EMERGENCY MASS NOTIFICATION SYSTEM (9 nodes)
  // ===================================================================

  "Emergency Mass Notification System": {
    summary:
      "Triggers on emergency events, requires two-person approval before sending, resolves the target audience, formats messages for each channel, sends parallel SMS, email, and Slack notifications, confirms delivery, creates an incident channel, logs everything for audit, and generates an ops summary.",
    nodes: [
      trigger("Webhook: Emergency event declared", "webhook_app", {
        source: "PagerDuty / Incident System / Manual",
        event: "emergency.declared",
        howItStarts:
          "An emergency is declared via PagerDuty incident, monitoring alert, or manual trigger requiring mass notification",
        setup:
          "Connect PagerDuty -> Webhooks -> V3 to POST incident data. Or configure your monitoring system (Datadog, Grafana) to POST critical alerts. Manual trigger also supported via API POST with severity, description, and affected systems.",
        inputFields: [],
      }),

      // 2. Two-person approval gate
      n("2", "approvalGate", "Two-person approval required", 280, {
        approvalGateData: {
          message:
            "EMERGENCY MASS NOTIFICATION REQUEST\n\nReview this emergency before mass notification is sent. This will notify ALL affected employees and stakeholders via SMS, email, and Slack simultaneously.\n\nPayload: {webhook_payload}\n\nApprove ONLY if this is a genuine emergency requiring immediate mass notification.",
        },
      }),

      // 3. Audience resolution
      aiNode(
        "3",
        "AI resolve audience and severity",
        520,
        "You are an emergency communications coordinator. Analyze this emergency event and determine: severity level (P0/P1/P2), affected teams, notification audience (all_hands, engineering, leadership, specific_team), estimated impact, and draft the emergency message. Extract the emergency details regardless of payload format. Respond JSON: {severity: string, emergency_title: string, emergency_description: string, affected_systems: string, audience: string, affected_teams: string[], estimated_impact: string, recommended_actions: string, incident_commander: string}",
        "{webhook_payload}",
        [
          { field: "severity", as: "severity", type: "string" },
          { field: "emergency_title", as: "emergency_title", type: "string" },
          {
            field: "emergency_description",
            as: "emergency_description",
            type: "string",
          },
          { field: "affected_systems", as: "affected_systems", type: "string" },
          { field: "audience", as: "notification_audience", type: "string" },
          { field: "affected_teams", as: "affected_teams", type: "string" },
          { field: "estimated_impact", as: "estimated_impact", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "incident_commander",
            as: "incident_commander",
            type: "string",
          },
        ],
      ),

      // 4. Channel formatting
      n("4", "runCode", "Format messages per channel", 760, {
        codeDetails: {
          language: "javascript",
          code: `// Format emergency messages for each delivery channel
const sms_message = '🚨 EMERGENCY [' + inputs.severity + ']: ' + inputs.emergency_title + '. ' + inputs.emergency_description + '. Actions: ' + inputs.recommended_actions + '. IC: ' + inputs.incident_commander;
const email_subject = '🚨 [' + inputs.severity + '] EMERGENCY: ' + inputs.emergency_title;
const email_body = '<h1>🚨 Emergency Alert: ' + inputs.emergency_title + '</h1>' +
  '<p><strong>Severity:</strong> ' + inputs.severity + '</p>' +
  '<p><strong>Description:</strong> ' + inputs.emergency_description + '</p>' +
  '<p><strong>Affected Systems:</strong> ' + inputs.affected_systems + '</p>' +
  '<p><strong>Impact:</strong> ' + inputs.estimated_impact + '</p>' +
  '<p><strong>Recommended Actions:</strong> ' + inputs.recommended_actions + '</p>' +
  '<p><strong>Incident Commander:</strong> ' + inputs.incident_commander + '</p>' +
  '<p>Join the incident channel immediately for updates.</p>';
const slack_message = '🚨 *EMERGENCY ALERT [' + inputs.severity + ']*\\n*' + inputs.emergency_title + '*\\n' + inputs.emergency_description + '\\n*Affected:* ' + inputs.affected_systems + '\\n*Impact:* ' + inputs.estimated_impact + '\\n*Actions:* ' + inputs.recommended_actions + '\\n*IC:* ' + inputs.incident_commander;

return { sms_message, email_subject, email_body, slack_message, channels_prepared: '3' };`,
          inputVariables: [
            { name: "severity", type: "string" },
            { name: "emergency_title", type: "string" },
            { name: "emergency_description", type: "string" },
            { name: "affected_systems", type: "string" },
            { name: "estimated_impact", type: "string" },
            { name: "recommended_actions", type: "string" },
            { name: "incident_commander", type: "string" },
          ],
          outputVariables: [
            { name: "sms_message", type: "string" },
            { name: "email_subject", type: "string" },
            { name: "email_body", type: "string" },
            { name: "slack_message", type: "string" },
            { name: "channels_prepared", type: "string" },
          ],
        },
      }),

      // 5. Parallel SMS delivery
      n("5", "sendSMS", "SMS: Emergency mass notification", 1000, {
        sendSmsDetails: {
          to: "{emergency_sms_list}",
          message: "{sms_message}",
        },
      }),

      // 6. Email delivery
      emailNode(
        "6",
        "Email: Emergency mass notification",
        1000,
        "{emergency_email_list}",
        "{email_subject}",
        "{email_body}",
        500,
      ),

      // 7. Slack delivery + incident channel
      slackNode("7", "Slack: Emergency broadcast", 1240, "{slack_message}"),

      // 8. Audit logging
      n("8", "hitApi", "Log emergency to audit trail", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/EmergencyLog!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{severity}","{emergency_title}","{notification_audience}","{affected_systems}","{incident_commander}","{channels_prepared}","{estimated_impact}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "audit_logged",
              type: "number",
            },
          ],
        },
      }),

      // 9. Ops summary
      slackNode(
        "9",
        "Ops: Notification delivery summary",
        1720,
        "✅ *Emergency Notification Delivery Complete*\n*Severity:* {severity}\n*Title:* {emergency_title}\n*Audience:* {notification_audience}\n*Channels sent:* SMS, Email, Slack ({channels_prepared} channels)\n*IC:* {incident_commander}\n*Audit logged:* Yes",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("4", "6"),
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 214. EMPLOYEE ADVOCACY PROMPTS (7 nodes)
  // ===================================================================

  "Employee Advocacy Prompts": {
    summary:
      "Triggers when company content is published, uses AI to assess shareability, generates role-specific social posts, formats for multiple platforms, calculates advocacy reward tokens, distributes via Slack to employees, and tracks participation in Google Sheets.",
    nodes: [
      trigger("Webhook: Company content published", "webhook_app", {
        source: "CMS / Blog / PR Platform",
        event: "content.published",
        howItStarts:
          "New company content is published (blog post, press release, product announcement, case study) and is ready for employee sharing",
        setup:
          "Connect your CMS (WordPress, Ghost, Contentful) or PR platform to POST published content data (title, URL, excerpt, category) to the workflow URL.",
        inputFields: [],
      }),

      // 2. AI shareability assessment
      aiNode(
        "2",
        "AI assess shareability",
        280,
        "You are a social media strategist. Analyze this published content and assess its shareability potential for employee advocacy. Find the title, URL, excerpt, and category regardless of field naming. Score shareability, identify the best employee segments to share it, and determine urgency. Respond JSON: {content_title: string, content_url: string, content_excerpt: string, content_category: string, shareability_score: number, best_segments: string[], share_urgency: string, key_talking_points: string[], suggested_hashtags: string[]}",
        "{webhook_payload}",
        [
          { field: "content_title", as: "content_title", type: "string" },
          { field: "content_url", as: "content_url", type: "string" },
          { field: "content_excerpt", as: "content_excerpt", type: "string" },
          { field: "content_category", as: "content_category", type: "string" },
          {
            field: "shareability_score",
            as: "shareability_score",
            type: "number",
          },
          { field: "best_segments", as: "best_segments", type: "string" },
          { field: "share_urgency", as: "share_urgency", type: "string" },
          {
            field: "key_talking_points",
            as: "key_talking_points",
            type: "string",
          },
          {
            field: "suggested_hashtags",
            as: "suggested_hashtags",
            type: "string",
          },
        ],
      ),

      // 3. AI role-specific posts
      aiNode(
        "3",
        "AI generate role-specific posts",
        520,
        "You are an employee advocacy copywriter. Create ready-to-share social posts for different employee roles. Each post should feel authentic and personal — not corporate. Create posts for: executives (thought leadership), sales (value-focused), engineers (technical angle), and general employees (company pride). Respond JSON: {executive_post: string, sales_post: string, engineer_post: string, general_post: string, linkedin_version: string, twitter_version: string}",
        "Content: {content_title}. URL: {content_url}. Excerpt: {content_excerpt}. Category: {content_category}. Talking points: {key_talking_points}. Hashtags: {suggested_hashtags}",
        [
          { field: "executive_post", as: "executive_post", type: "string" },
          { field: "sales_post", as: "sales_post", type: "string" },
          { field: "engineer_post", as: "engineer_post", type: "string" },
          { field: "general_post", as: "general_post", type: "string" },
          { field: "linkedin_version", as: "linkedin_version", type: "string" },
          { field: "twitter_version", as: "twitter_version", type: "string" },
        ],
      ),

      // 4. Platform formatting
      aiNode(
        "4",
        "AI format for each platform",
        760,
        "You are a social media formatting expert. Take these advocacy posts and optimize them for LinkedIn and X (Twitter). LinkedIn: longer, professional, use line breaks. X: concise under 280 chars, punchy, thread-friendly. Ensure the content URL is included in each. Respond JSON: {linkedin_exec: string, linkedin_sales: string, linkedin_eng: string, linkedin_general: string, x_exec: string, x_sales: string, x_eng: string, x_general: string}",
        "Executive: {executive_post}. Sales: {sales_post}. Engineer: {engineer_post}. General: {general_post}. URL: {content_url}. Hashtags: {suggested_hashtags}",
        [
          { field: "linkedin_exec", as: "linkedin_exec", type: "string" },
          { field: "linkedin_sales", as: "linkedin_sales", type: "string" },
          { field: "linkedin_eng", as: "linkedin_eng", type: "string" },
          { field: "linkedin_general", as: "linkedin_general", type: "string" },
          { field: "x_exec", as: "x_exec", type: "string" },
          { field: "x_sales", as: "x_sales", type: "string" },
          { field: "x_eng", as: "x_eng", type: "string" },
          { field: "x_general", as: "x_general", type: "string" },
        ],
      ),

      // 5. Calculate advocacy tokens
      n("5", "runCode", "Calculate advocacy reward tokens", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Calculate advocacy reward tokens based on content value and urgency
const baseTokens = 10;
const urgencyMultiplier = { high: 3, medium: 2, low: 1 };
const categoryBonus = { product_launch: 5, case_study: 4, press: 3, blog: 2 };

const urgencyMult = urgencyMultiplier[inputs.share_urgency] || 1;
const catBonus = categoryBonus[inputs.content_category] || 1;
const shareabilityBonus = Math.round(inputs.shareability_score * 5);
const total_tokens = baseTokens * urgencyMult + catBonus + shareabilityBonus;

const token_breakdown = 'Base: ' + baseTokens + ' x Urgency(' + inputs.share_urgency + '): ' + urgencyMult + ' + Category(' + inputs.content_category + '): ' + catBonus + ' + Shareability: ' + shareabilityBonus + ' = ' + total_tokens;

return { total_tokens: String(total_tokens), token_breakdown, reward_ready: 'true' };`,
          inputVariables: [
            { name: "share_urgency", type: "string" },
            { name: "content_category", type: "string" },
            { name: "shareability_score", type: "number" },
          ],
          outputVariables: [
            { name: "total_tokens", type: "string" },
            { name: "token_breakdown", type: "string" },
            { name: "reward_ready", type: "string" },
          ],
        },
      }),

      // 6. Slack distribution
      slackNode(
        "6",
        "Slack: Distribute advocacy prompts",
        1240,
        "📣 *Employee Advocacy Opportunity*\n🏷️ *{content_category}* | Urgency: {share_urgency} | 🪙 {total_tokens} tokens\n\n*{content_title}*\n{content_url}\n\n*LinkedIn Posts (copy & share):*\n👔 Executive: {linkedin_exec}\n💼 Sales: {linkedin_sales}\n👩‍💻 Engineering: {linkedin_eng}\n🙌 General: {linkedin_general}\n\n*X Posts:*\n{x_exec}\n\n*Hashtags:* {suggested_hashtags}",
      ),

      // 7. Tracking
      n("7", "hitApi", "Log advocacy campaign to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Advocacy!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{content_title}","{content_category}","{shareability_score}","{share_urgency}","{total_tokens}","{best_segments}","{content_url}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "advocacy_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 215. EVENT LIVE-POSTING PREP (7 nodes)
  // ===================================================================

  "Event Live-Posting Prep": {
    summary:
      "Ingests event details, researches speakers via SerpAPI, generates AI social posts for live coverage, creates placeholder posts with timing markers, formats for each social platform, queues the posting plan in Google Sheets, and notifies the social media team.",
    nodes: [
      trigger("Webhook: Event details submitted", "webhook_app", {
        source: "Event Platform / Manual",
        event: "event.scheduled",
        howItStarts:
          "An upcoming event is registered for live social media coverage, providing event details, speaker list, and schedule",
        setup:
          "POST event data (event name, date, speakers list with names/titles/companies, session schedule, hashtags) to the workflow URL from your event platform (Eventbrite, Luma, Hopin) or manually via API.",
        inputFields: [],
      }),

      // 2. Speaker research via SerpAPI
      aiNode(
        "2",
        "AI extract event details",
        280,
        "You are an event coordinator assistant. Extract event details from this payload regardless of field naming: event name, date, venue, speakers (names, titles, companies), schedule, hashtags, and social handles. Respond JSON: {event_name: string, event_date: string, venue: string, speakers: string, schedule: string, hashtags: string, social_handles: string, speaker_count: number}",
        "{webhook_payload}",
        [
          { field: "event_name", as: "event_name", type: "string" },
          { field: "event_date", as: "event_date", type: "string" },
          { field: "venue", as: "venue", type: "string" },
          { field: "speakers", as: "speakers", type: "string" },
          { field: "schedule", as: "schedule", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
          { field: "social_handles", as: "social_handles", type: "string" },
          { field: "speaker_count", as: "speaker_count", type: "number" },
        ],
      ),

      // 3. SerpAPI speaker research
      n("3", "hitApi", "SerpAPI: Research speakers", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={speakers}+speaker+bio&num=10&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "speaker_research",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI post generation
      aiNode(
        "4",
        "AI generate event posts",
        760,
        "You are a social media strategist for live event coverage. Given event details, speaker research, and schedule, create a comprehensive set of social posts: pre-event hype (3 posts), per-speaker intro posts, live session highlight templates, and wrap-up posts. Each post should include relevant hashtags and speaker mentions. Respond JSON: {pre_event_posts: string[], speaker_intro_posts: string[], session_templates: string[], wrap_up_posts: string[], total_posts_planned: number}",
        "Event: {event_name}. Date: {event_date}. Venue: {venue}. Speakers: {speakers}. Schedule: {schedule}. Hashtags: {hashtags}. Social handles: {social_handles}. Speaker research: {speaker_research}",
        [
          { field: "pre_event_posts", as: "pre_event_posts", type: "string" },
          {
            field: "speaker_intro_posts",
            as: "speaker_intro_posts",
            type: "string",
          },
          {
            field: "session_templates",
            as: "session_templates",
            type: "string",
          },
          { field: "wrap_up_posts", as: "wrap_up_posts", type: "string" },
          {
            field: "total_posts_planned",
            as: "total_posts_planned",
            type: "number",
          },
        ],
      ),

      // 5. Placeholder + timing markers
      n("5", "runCode", "Create timed posting placeholders", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Create timed posting queue with placeholders
const pre = typeof inputs.pre_event_posts === 'string' ? JSON.parse(inputs.pre_event_posts || '[]') : inputs.pre_event_posts;
const speakers = typeof inputs.speaker_intro_posts === 'string' ? JSON.parse(inputs.speaker_intro_posts || '[]') : inputs.speaker_intro_posts;
const sessions = typeof inputs.session_templates === 'string' ? JSON.parse(inputs.session_templates || '[]') : inputs.session_templates;
const wrap = typeof inputs.wrap_up_posts === 'string' ? JSON.parse(inputs.wrap_up_posts || '[]') : inputs.wrap_up_posts;

const queue = [];
// Pre-event: 3 days, 1 day, 1 hour before
const preTimings = ['-3d', '-1d', '-1h'];
pre.forEach((p, i) => queue.push({ time: preTimings[i] || '-1h', type: 'pre_event', content: p, platform: 'linkedin,x' }));

// Speaker intros: day before, staggered
speakers.forEach((s, i) => queue.push({ time: '-1d+' + (i * 2) + 'h', type: 'speaker_intro', content: s, platform: 'linkedin,x' }));

// Session templates: during event
sessions.forEach((s, i) => queue.push({ time: 'live+' + (i * 30) + 'm', type: 'session_live', content: s, platform: 'x' }));

// Wrap-up: after event
wrap.forEach((w, i) => queue.push({ time: '+' + (i + 1) + 'h', type: 'wrap_up', content: w, platform: 'linkedin,x' }));

return { posting_queue: JSON.stringify(queue), total_queued: String(queue.length), queue_ready: 'true' };`,
          inputVariables: [
            { name: "pre_event_posts", type: "string" },
            { name: "speaker_intro_posts", type: "string" },
            { name: "session_templates", type: "string" },
            { name: "wrap_up_posts", type: "string" },
          ],
          outputVariables: [
            { name: "posting_queue", type: "string" },
            { name: "total_queued", type: "string" },
            { name: "queue_ready", type: "string" },
          ],
        },
      }),

      // 6. Queue plan to Sheets
      n("6", "hitApi", "Save posting queue to Google Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/EventPosts!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{event_name}","{event_date}","{total_queued}","{posting_queue}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "queue_saved",
              type: "number",
            },
          ],
        },
      }),

      // 7. Team notification
      slackNode(
        "7",
        "Slack: Notify social team",
        1480,
        "📅 *Event Live-Posting Plan Ready*\n*Event:* {event_name} — {event_date}\n*Venue:* {venue}\n*Speakers:* {speaker_count}\n*Posts queued:* {total_queued}\n*Hashtags:* {hashtags}\n\nThe posting queue has been saved to the Event Posts sheet. Review and approve posts before the event.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 216. FOUNDER POSTING ASSISTANT (7 nodes)
  // ===================================================================

  "Founder Posting Assistant": {
    summary:
      "Takes a founder's raw idea (text or voice memo via Whisper transcription), expands it with AI, calibrates the voice to match the founder's style, generates LinkedIn and X variants, routes through founder approval, and schedules approved posts with logging.",
    nodes: [
      trigger("Webhook: Founder idea submitted", "webhook_app", {
        source: "Slack / Voice Memo / API",
        event: "idea.submitted",
        howItStarts:
          "A founder submits a raw idea via Slack message, voice memo upload, or direct text input for social media expansion",
        setup:
          "Create a Slack slash-command (/founder-post) or webhook that POSTs the idea text (or audio file URL for voice memos) to the workflow URL. Include the founder name and target platforms.",
        inputFields: [],
      }),

      // 2. Optional Whisper transcription (handles voice memos)
      aiNode(
        "2",
        "AI extract idea or transcribe",
        280,
        "You are a founder content assistant. Extract the idea text from this payload. If it contains an audio_url or voice_memo field, note it for transcription. Find the founder name, idea text (or audio URL), and target platforms regardless of field naming. Respond JSON: {founder_name: string, raw_idea: string, audio_url: string, target_platforms: string, is_voice_memo: string, idea_topic: string}",
        "{webhook_payload}",
        [
          { field: "founder_name", as: "founder_name", type: "string" },
          { field: "raw_idea", as: "raw_idea", type: "string" },
          { field: "audio_url", as: "audio_url", type: "string" },
          { field: "target_platforms", as: "target_platforms", type: "string" },
          { field: "is_voice_memo", as: "is_voice_memo", type: "string" },
          { field: "idea_topic", as: "idea_topic", type: "string" },
        ],
      ),

      // 3. AI expansion
      aiNode(
        "3",
        "AI expand idea into content",
        520,
        "You are a thought leadership ghostwriter for startup founders. Take this raw idea and expand it into a compelling, insightful post. Add context, examples, or frameworks that make the idea shareable. Keep the original voice and perspective. Do not make it corporate or generic. Respond JSON: {expanded_content: string, key_insight: string, hook: string, cta: string, estimated_engagement: string}",
        "Founder: {founder_name}. Topic: {idea_topic}. Raw idea: {raw_idea}",
        [
          { field: "expanded_content", as: "expanded_content", type: "string" },
          { field: "key_insight", as: "key_insight", type: "string" },
          { field: "hook", as: "post_hook", type: "string" },
          { field: "cta", as: "post_cta", type: "string" },
          {
            field: "estimated_engagement",
            as: "estimated_engagement",
            type: "string",
          },
        ],
      ),

      // 4. Voice calibration
      aiNode(
        "4",
        "AI calibrate founder voice",
        760,
        "You are a voice calibration specialist. Adjust this expanded content to match the founders personal brand voice. Make it sound like them — direct, authentic, with their typical speech patterns. Remove any ghostwriter feel. The post should read as if the founder typed it themselves. Respond JSON: {calibrated_content: string, voice_match_score: number, tone_adjustments: string}",
        "Founder: {founder_name}. Original idea: {raw_idea}. Expanded: {expanded_content}. Hook: {post_hook}. CTA: {post_cta}",
        [
          {
            field: "calibrated_content",
            as: "calibrated_content",
            type: "string",
          },
          {
            field: "voice_match_score",
            as: "voice_match_score",
            type: "number",
          },
          { field: "tone_adjustments", as: "tone_adjustments", type: "string" },
        ],
      ),

      // 5. LinkedIn/X variants
      aiNode(
        "5",
        "AI generate LinkedIn and X variants",
        1000,
        "You are a social media formatting expert. Take this calibrated founder content and create optimized variants for LinkedIn and X (Twitter). LinkedIn: professional thought leadership, use line breaks and emojis sparingly, 1300 chars max. X: punchy, under 280 chars for main tweet, optional thread of 2-3 tweets. Respond JSON: {linkedin_post: string, x_main_tweet: string, x_thread: string[], optimal_posting_time: string}",
        "Calibrated content: {calibrated_content}. Hook: {post_hook}. CTA: {post_cta}. Key insight: {key_insight}",
        [
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "x_main_tweet", as: "x_main_tweet", type: "string" },
          { field: "x_thread", as: "x_thread", type: "string" },
          {
            field: "optimal_posting_time",
            as: "optimal_posting_time",
            type: "string",
          },
        ],
      ),

      // 6. Founder approval gate
      n("6", "approvalGate", "Founder approval before publishing", 1240, {
        approvalGateData: {
          message:
            "Review your posts, {founder_name}:\n\n--- LINKEDIN ---\n{linkedin_post}\n\n--- X (MAIN) ---\n{x_main_tweet}\n\n--- X (THREAD) ---\n{x_thread}\n\nVoice match: {voice_match_score}/1.0\nEstimated engagement: {estimated_engagement}\nOptimal posting time: {optimal_posting_time}\n\nApprove to schedule.",
        },
      }),

      // 7. Schedule + log
      n("7", "hitApi", "Schedule posts + log to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/FounderPosts!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{founder_name}","{idea_topic}","{linkedin_post}","{x_main_tweet}","{optimal_posting_time}","{voice_match_score}","{estimated_engagement}","approved"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "posts_scheduled",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 217. GITHUB ISSUE TO SLACK ALERT (7 nodes)
  // ===================================================================

  "GitHub Issue to Slack Alert": {
    summary:
      "Receives GitHub issue webhooks, uses AI to classify severity and impact, calculates SLA deadlines, routes alerts to the appropriate Slack channel, posts formatted notifications, monitors for SLA breaches with a delay node, and logs resolution metrics.",
    nodes: [
      trigger("Webhook: GitHub issue created", "webhook_app", {
        source: "GitHub",
        event: "issues.opened",
        howItStarts: "A new issue is created in a monitored GitHub repository",
        setup:
          "In GitHub repo -> Settings -> Webhooks, add a webhook for 'Issues' events pointing to the workflow URL. Select 'application/json' content type.",
        inputFields: [],
      }),

      // 2. AI severity classification
      aiNode(
        "2",
        "AI classify issue severity",
        280,
        "You are a software engineering triage specialist. Analyze this GitHub issue webhook and classify it. Find the issue title, body, labels, author, and repo regardless of payload format. Classify severity (P0-critical, P1-high, P2-medium, P3-low) based on content. Respond JSON: {issue_title: string, issue_body: string, issue_number: number, repo_name: string, author: string, labels: string, severity: string, impact_area: string, affected_users: string, is_regression: string}",
        "{webhook_payload}",
        [
          { field: "issue_title", as: "issue_title", type: "string" },
          { field: "issue_body", as: "issue_body", type: "string" },
          { field: "issue_number", as: "issue_number", type: "number" },
          { field: "repo_name", as: "repo_name", type: "string" },
          { field: "author", as: "issue_author", type: "string" },
          { field: "labels", as: "issue_labels", type: "string" },
          { field: "severity", as: "severity", type: "string" },
          { field: "impact_area", as: "impact_area", type: "string" },
          { field: "affected_users", as: "affected_users", type: "string" },
          { field: "is_regression", as: "is_regression", type: "string" },
        ],
      ),

      // 3. SLA calculation
      n("3", "runCode", "Calculate SLA deadline", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Calculate SLA response and resolution deadlines based on severity
const slaMap = {
  'P0-critical': { response_hours: 0.25, resolution_hours: 4, channel: '#eng-critical' },
  'P1-high': { response_hours: 1, resolution_hours: 24, channel: '#eng-urgent' },
  'P2-medium': { response_hours: 4, resolution_hours: 72, channel: '#eng-triage' },
  'P3-low': { response_hours: 24, resolution_hours: 168, channel: '#eng-backlog' }
};
const sla = slaMap[inputs.severity] || slaMap['P2-medium'];
const now = new Date();
const response_deadline = new Date(now.getTime() + sla.response_hours * 3600000).toISOString();
const resolution_deadline = new Date(now.getTime() + sla.resolution_hours * 3600000).toISOString();

return {
  response_deadline,
  resolution_deadline,
  response_hours: String(sla.response_hours),
  resolution_hours: String(sla.resolution_hours),
  target_channel: sla.channel,
  sla_calculated: 'true'
};`,
          inputVariables: [{ name: "severity", type: "string" }],
          outputVariables: [
            { name: "response_deadline", type: "string" },
            { name: "resolution_deadline", type: "string" },
            { name: "response_hours", type: "string" },
            { name: "resolution_hours", type: "string" },
            { name: "target_channel", type: "string" },
            { name: "sla_calculated", type: "string" },
          ],
        },
      }),

      // 4. Slack routing
      slackNode(
        "4",
        "Post issue alert to Slack",
        760,
        "🐛 *New GitHub Issue [{severity}]*\n*#{issue_number}:* {issue_title}\n*Repo:* {repo_name} | *Author:* {issue_author}\n*Impact:* {impact_area} | *Affected users:* {affected_users}\n*Regression:* {is_regression} | *Labels:* {issue_labels}\n*SLA Response:* {response_hours}h (by {response_deadline})\n*SLA Resolution:* {resolution_hours}h (by {resolution_deadline})\n\n{issue_body}",
      ),

      // 5. Post to target channel
      slackNode(
        "5",
        "Post to team-specific channel",
        1000,
        "📋 *Issue assigned to this channel*\n*#{issue_number}:* {issue_title} [{severity}]\nResponse SLA: {response_hours}h | Resolution SLA: {resolution_hours}h\nPlease acknowledge within SLA window.",
      ),

      // 6. SLA breach monitor delay
      n("6", "delayAction", "Wait for SLA response window", 1240, {
        delayAction: { time: 1, timeFormat: "hours" },
      }),

      // 7. Resolution logging
      n("7", "hitApi", "Log issue + SLA to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/GitHubSLA!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{repo_name}","#{issue_number}","{issue_title}","{severity}","{response_deadline}","{resolution_deadline}","{impact_area}","{issue_author}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "sla_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 218. GITHUB PR TO SLACK NOTIFIER (7 nodes)
  // ===================================================================

  "GitHub PR to Slack Notifier": {
    summary:
      "Receives GitHub PR webhooks, hydrates PR data from the GitHub API, uses AI to classify the change impact, formats a Slack alert with context, posts to the appropriate channel, tracks review SLA with a delay, and logs metrics for engineering analytics.",
    nodes: [
      trigger("Webhook: GitHub PR opened", "webhook_app", {
        source: "GitHub",
        event: "pull_request.opened",
        howItStarts:
          "A new pull request is opened in a monitored GitHub repository",
        setup:
          "In GitHub repo -> Settings -> Webhooks, add a webhook for 'Pull requests' events pointing to the workflow URL. Select 'application/json' content type.",
        inputFields: [],
      }),

      // 2. PR hydration via GitHub API
      n("2", "hitApi", "GitHub API: Hydrate PR details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.github.com/repos/{repo_owner}/{repo_name}/pulls/{pr_number}",
          headers: [
            { key: "Authorization", value: "Bearer {github_token}" },
            { key: "Accept", value: "application/vnd.github.v3+json" },
          ],
          body: [],
          variables: [
            { name: "title", customName: "pr_title", type: "string" },
            { name: "body", customName: "pr_body", type: "string" },
            { name: "number", customName: "pr_number", type: "number" },
            { name: "user.login", customName: "pr_author", type: "string" },
            { name: "base.ref", customName: "base_branch", type: "string" },
            { name: "head.ref", customName: "head_branch", type: "string" },
            { name: "additions", customName: "lines_added", type: "number" },
            { name: "deletions", customName: "lines_deleted", type: "number" },
            {
              name: "changed_files",
              customName: "files_changed",
              type: "number",
            },
            { name: "html_url", customName: "pr_url", type: "string" },
            {
              name: "requested_reviewers",
              customName: "reviewers",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI impact classification
      aiNode(
        "3",
        "AI classify PR impact",
        520,
        "You are a senior software engineer. Analyze this PR and classify its impact: breaking_change, feature, bugfix, refactor, docs, test, ci, or chore. Estimate review complexity (trivial, moderate, complex, critical). Identify risk areas. Respond JSON: {change_type: string, review_complexity: string, risk_areas: string[], summary: string, suggested_reviewers: string[], estimated_review_time_minutes: number, breaking_changes_detected: string}",
        "PR #{pr_number}: {pr_title}. Branch: {head_branch} -> {base_branch}. Body: {pr_body}. +{lines_added}/-{lines_deleted} in {files_changed} files. Author: {pr_author}",
        [
          { field: "change_type", as: "change_type", type: "string" },
          {
            field: "review_complexity",
            as: "review_complexity",
            type: "string",
          },
          { field: "risk_areas", as: "risk_areas", type: "string" },
          { field: "summary", as: "pr_summary", type: "string" },
          {
            field: "suggested_reviewers",
            as: "suggested_reviewers",
            type: "string",
          },
          {
            field: "estimated_review_time_minutes",
            as: "estimated_review_time",
            type: "number",
          },
          {
            field: "breaking_changes_detected",
            as: "breaking_changes",
            type: "string",
          },
        ],
      ),

      // 4. Slack alert
      slackNode(
        "4",
        "Post PR alert to Slack",
        760,
        "🔀 *New PR [{change_type}]*\n*#{pr_number}:* {pr_title}\n*Author:* {pr_author} | *Branch:* {head_branch} → {base_branch}\n*Size:* +{lines_added}/-{lines_deleted} in {files_changed} files\n*Complexity:* {review_complexity} (~{estimated_review_time}min)\n*Risk:* {risk_areas}\n*Breaking:* {breaking_changes}\n*Summary:* {pr_summary}\n*Reviewers:* {suggested_reviewers}\n🔗 {pr_url}",
      ),

      // 5. Channel posting
      slackNode(
        "5",
        "Post to review channel",
        1000,
        "📋 *Review needed:* PR #{pr_number} — {pr_title}\nComplexity: {review_complexity} | Type: {change_type}\nEstimated time: ~{estimated_review_time} minutes\n{pr_url}",
      ),

      // 6. Review SLA tracking delay
      n("6", "delayAction", "Wait for review SLA window", 1240, {
        delayAction: { time: 4, timeFormat: "hours" },
      }),

      // 7. Metrics logging
      n("7", "hitApi", "Log PR metrics to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/PRMetrics!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["#{pr_number}","{pr_title}","{pr_author}","{change_type}","{review_complexity}","{lines_added}","{lines_deleted}","{files_changed}","{estimated_review_time}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "pr_metrics_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 219. GITHUB SLACK PR ALERTS (7 nodes)
  // ===================================================================

  "GitHub Slack PR Alerts": {
    summary:
      "Monitors PR status changes, aggregates metrics with runCode, calculates response times, generates AI-powered alert summaries, routes by severity, delivers alerts to Slack, and sends weekly engineering metrics via email.",
    nodes: [
      trigger("Webhook: GitHub PR status change", "webhook_app", {
        source: "GitHub",
        event: "pull_request.*",
        howItStarts:
          "A pull request status changes (opened, review requested, approved, merged, closed) in a monitored repository",
        setup:
          "In GitHub repo -> Settings -> Webhooks, add a webhook for 'Pull requests' and 'Pull request reviews' events. Point to the workflow URL.",
        inputFields: [],
      }),

      // 2. Aggregation
      n("2", "runCode", "Aggregate PR status data", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Parse PR webhook and aggregate status data
const raw = typeof inputs.webhook_payload === 'string' ? JSON.parse(inputs.webhook_payload) : inputs.webhook_payload;
const action = raw.action || 'unknown';
const pr = raw.pull_request || {};
const pr_number = pr.number || 0;
const pr_title = pr.title || 'Unknown';
const pr_author = pr.user?.login || 'unknown';
const repo = raw.repository?.full_name || 'unknown';
const pr_url = pr.html_url || '';
const created_at = pr.created_at || '';
const updated_at = pr.updated_at || '';
const review = raw.review || {};
const reviewer = review.user?.login || '';
const review_state = review.state || action;
const base_branch = pr.base?.ref || 'main';
const additions = pr.additions || 0;
const deletions = pr.deletions || 0;

return { action, pr_number: String(pr_number), pr_title, pr_author, repo, pr_url, created_at, updated_at, reviewer, review_state, base_branch, additions: String(additions), deletions: String(deletions) };`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "action", type: "string" },
            { name: "pr_number", type: "string" },
            { name: "pr_title", type: "string" },
            { name: "pr_author", type: "string" },
            { name: "repo", type: "string" },
            { name: "pr_url", type: "string" },
            { name: "created_at", type: "string" },
            { name: "updated_at", type: "string" },
            { name: "reviewer", type: "string" },
            { name: "review_state", type: "string" },
            { name: "base_branch", type: "string" },
            { name: "additions", type: "string" },
            { name: "deletions", type: "string" },
          ],
        },
      }),

      // 3. Response time calculation
      n("3", "runCode", "Calculate response time metrics", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Calculate time metrics between PR creation and current action
const created = new Date(inputs.created_at);
const updated = new Date(inputs.updated_at);
const now = new Date();

const response_time_hours = Math.round((updated - created) / 3600000 * 10) / 10;
const age_hours = Math.round((now - created) / 3600000 * 10) / 10;
const is_stale = age_hours > 48 ? 'true' : 'false';
const sla_status = response_time_hours <= 4 ? 'within_sla' : response_time_hours <= 24 ? 'warning' : 'breached';

return { response_time_hours: String(response_time_hours), age_hours: String(age_hours), is_stale, sla_status };`,
          inputVariables: [
            { name: "created_at", type: "string" },
            { name: "updated_at", type: "string" },
          ],
          outputVariables: [
            { name: "response_time_hours", type: "string" },
            { name: "age_hours", type: "string" },
            { name: "is_stale", type: "string" },
            { name: "sla_status", type: "string" },
          ],
        },
      }),

      // 4. AI alert generation
      aiNode(
        "4",
        "AI generate alert summary",
        760,
        "You are an engineering metrics analyst. Generate a concise alert summary for this PR status change. Include context about response time, SLA compliance, and any action needed. Rate the alert severity (info, warning, urgent). Respond JSON: {alert_summary: string, alert_severity: string, action_needed: string, metric_highlight: string}",
        "PR #{pr_number}: {pr_title} by {pr_author} in {repo}. Action: {action}. Review state: {review_state}. Reviewer: {reviewer}. Response time: {response_time_hours}h. Age: {age_hours}h. SLA: {sla_status}. Stale: {is_stale}. Size: +{additions}/-{deletions}",
        [
          { field: "alert_summary", as: "alert_summary", type: "string" },
          { field: "alert_severity", as: "alert_severity", type: "string" },
          { field: "action_needed", as: "action_needed", type: "string" },
          { field: "metric_highlight", as: "metric_highlight", type: "string" },
        ],
      ),

      // 5. Severity condition
      n("5", "condition", "Alert severity: urgent or warning?", 1000, {
        conditionDetails: {
          conditions: [
            {
              field: "alert_severity",
              operator: "in",
              value: "urgent,warning",
            },
          ],
        },
      }),

      // 6a. True: Urgent Slack delivery
      slackNode(
        "6",
        "Slack: Urgent PR alert",
        1240,
        "⚠️ *PR Alert [{alert_severity}]*\n*#{pr_number}:* {pr_title}\n*Repo:* {repo} | *Author:* {pr_author}\n*Action:* {action} | *Reviewer:* {reviewer}\n*Response Time:* {response_time_hours}h | *SLA:* {sla_status}\n*Summary:* {alert_summary}\n*Action Needed:* {action_needed}\n{pr_url}",
      ),

      // 6b. False: Info-level Slack delivery
      slackNode(
        "6b",
        "Slack: Info PR update",
        1240,
        "ℹ️ PR #{pr_number}: {pr_title} — {action} by {reviewer || pr_author}\nResponse time: {response_time_hours}h | SLA: {sla_status}\n{pr_url}",
        500,
      ),

      // 7. Weekly metrics email
      emailNode(
        "7",
        "Weekly PR metrics email",
        1480,
        "{eng_lead_email}",
        "PR Metrics Update: #{pr_number} {pr_title}",
        "<p><strong>PR #{pr_number}:</strong> {pr_title}</p><p>Author: {pr_author} | Action: {action}</p><p>Response Time: {response_time_hours}h | SLA: {sla_status}</p><p>{alert_summary}</p><p>{metric_highlight}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6b", "output_2"),
      e("6", "7"),
      e("6b", "7"),
    ],
  },

  // ===================================================================
  // 220. GOOGLE ANALYTICS TO SLACK WEEKLY REPORT (7 nodes)
  // ===================================================================

  "Google Analytics to Slack Weekly Report": {
    summary:
      "Runs on a weekly schedule, pulls current and prior week GA4 data, uses AI to analyze trends and anomalies, generates a formatted report, delivers to Slack, and archives results in Google Sheets for historical tracking.",
    nodes: [
      trigger("Schedule: Weekly GA4 report", "schedule", {
        schedule: "Every Monday at 9:00 AM",
        howItStarts:
          "Runs automatically every Monday morning to generate the previous week's Google Analytics performance report",
        setup:
          "Configure Google Analytics Data API credentials. Set the GA4 property ID. The workflow will pull the previous 7 days' data and compare against the prior week.",
        inputFields: [],
      }),

      // 2. GA4 current week data
      n("2", "hitApi", "GA4: Pull current week data", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_analytics_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"7daysAgo","endDate":"yesterday"}]',
            },
            {
              key: "metrics",
              value:
                '[{"name":"sessions"},{"name":"totalUsers"},{"name":"newUsers"},{"name":"bounceRate"},{"name":"averageSessionDuration"},{"name":"conversions"},{"name":"totalRevenue"}]',
            },
            {
              key: "dimensions",
              value: '[{"name":"sessionDefaultChannelGroup"}]',
            },
          ],
          variables: [
            { name: "rows", customName: "current_week_data", type: "string" },
            {
              name: "totals",
              customName: "current_week_totals",
              type: "string",
            },
          ],
        },
      }),

      // 3. GA4 prior week data
      n("3", "hitApi", "GA4: Pull prior week data", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://analyticsdata.googleapis.com/v1beta/properties/{ga4_property_id}:runReport",
          headers: [
            { key: "Authorization", value: "Bearer {google_analytics_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "dateRanges",
              value: '[{"startDate":"14daysAgo","endDate":"8daysAgo"}]',
            },
            {
              key: "metrics",
              value:
                '[{"name":"sessions"},{"name":"totalUsers"},{"name":"newUsers"},{"name":"bounceRate"},{"name":"averageSessionDuration"},{"name":"conversions"},{"name":"totalRevenue"}]',
            },
            {
              key: "dimensions",
              value: '[{"name":"sessionDefaultChannelGroup"}]',
            },
          ],
          variables: [
            { name: "rows", customName: "prior_week_data", type: "string" },
            { name: "totals", customName: "prior_week_totals", type: "string" },
          ],
        },
      }),

      // 4. AI trend analysis
      aiNode(
        "4",
        "AI analyze trends and anomalies",
        760,
        "You are a digital analytics expert. Compare this weeks GA4 data against the prior week. Identify trends, anomalies, winning channels, declining channels, and actionable insights. Calculate week-over-week percentage changes for each metric. Highlight anything that changed more than 15%. Respond JSON: {executive_summary: string, sessions_wow_change: string, users_wow_change: string, revenue_wow_change: string, top_channel: string, declining_channel: string, anomalies: string[], key_insights: string[], recommended_actions: string[]}",
        "Current week: {current_week_data}. Totals: {current_week_totals}. Prior week: {prior_week_data}. Totals: {prior_week_totals}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "sessions_wow_change",
            as: "sessions_wow_change",
            type: "string",
          },
          { field: "users_wow_change", as: "users_wow_change", type: "string" },
          {
            field: "revenue_wow_change",
            as: "revenue_wow_change",
            type: "string",
          },
          { field: "top_channel", as: "top_channel", type: "string" },
          {
            field: "declining_channel",
            as: "declining_channel",
            type: "string",
          },
          { field: "anomalies", as: "anomalies", type: "string" },
          { field: "key_insights", as: "key_insights", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
        ],
      ),

      // 5. Build report
      n("5", "runCode", "Build formatted weekly report", 1000, {
        codeDetails: {
          language: "javascript",
          code: `// Build the formatted weekly GA4 report
const report = '📊 *Weekly Analytics Report*\\n' +
  '---\\n' +
  '*Executive Summary:* ' + inputs.executive_summary + '\\n\\n' +
  '*Week-over-Week Changes:*\\n' +
  '• Sessions: ' + inputs.sessions_wow_change + '\\n' +
  '• Users: ' + inputs.users_wow_change + '\\n' +
  '• Revenue: ' + inputs.revenue_wow_change + '\\n\\n' +
  '*Top Channel:* ' + inputs.top_channel + '\\n' +
  '*Declining Channel:* ' + inputs.declining_channel + '\\n\\n' +
  '*Anomalies:* ' + inputs.anomalies + '\\n\\n' +
  '*Key Insights:*\\n' + inputs.key_insights + '\\n\\n' +
  '*Recommended Actions:*\\n' + inputs.recommended_actions;

return { formatted_report: report, report_date: new Date().toISOString().split('T')[0] };`,
          inputVariables: [
            { name: "executive_summary", type: "string" },
            { name: "sessions_wow_change", type: "string" },
            { name: "users_wow_change", type: "string" },
            { name: "revenue_wow_change", type: "string" },
            { name: "top_channel", type: "string" },
            { name: "declining_channel", type: "string" },
            { name: "anomalies", type: "string" },
            { name: "key_insights", type: "string" },
            { name: "recommended_actions", type: "string" },
          ],
          outputVariables: [
            { name: "formatted_report", type: "string" },
            { name: "report_date", type: "string" },
          ],
        },
      }),

      // 6. Slack delivery
      slackNode("6", "Post weekly report to Slack", 1240, "{formatted_report}"),

      // 7. Archive to Sheets
      n("7", "hitApi", "Archive report to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/WeeklyGA!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{report_date}","{sessions_wow_change}","{users_wow_change}","{revenue_wow_change}","{top_channel}","{declining_channel}","{executive_summary}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "report_archived",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 221. INTERCOM CONVERSATION TO ZENDESK TICKET (7 nodes)
  // ===================================================================

  "Intercom Conversation to Zendesk Ticket": {
    summary:
      "Triggers on new Intercom conversations, hydrates the full conversation history, maps fields to the Zendesk schema, checks for duplicate tickets, creates or updates Zendesk tickets, enables bi-directional sync, and alerts on exceptions via Slack.",
    nodes: [
      trigger("Webhook: Intercom conversation created", "webhook_app", {
        source: "Intercom",
        event: "conversation.created",
        howItStarts:
          "A new conversation is created in Intercom that needs to be synced to Zendesk as a support ticket",
        setup:
          "In Intercom -> Settings -> Webhooks, add a webhook for 'conversation.created' events pointing to the workflow URL. Ensure Zendesk API credentials are configured.",
        inputFields: [],
      }),

      // 2. Conversation hydration
      n("2", "hitApi", "Intercom: Hydrate conversation", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.intercom.io/conversations/{conversation_id}",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "id", customName: "conversation_id", type: "string" },
            {
              name: "source.body",
              customName: "conversation_body",
              type: "string",
            },
            {
              name: "source.author.name",
              customName: "customer_name",
              type: "string",
            },
            {
              name: "source.author.email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "source.subject",
              customName: "conversation_subject",
              type: "string",
            },
            { name: "tags.tags", customName: "intercom_tags", type: "string" },
            {
              name: "priority",
              customName: "intercom_priority",
              type: "string",
            },
            { name: "state", customName: "conversation_state", type: "string" },
          ],
        },
      }),

      // 3. Schema mapping
      n("3", "runCode", "Map Intercom fields to Zendesk schema", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Map Intercom conversation data to Zendesk ticket schema
const priorityMap = { urgent: 'urgent', high: 'high', normal: 'normal', low: 'low' };
const zendesk_priority = priorityMap[inputs.intercom_priority] || 'normal';

const tags = typeof inputs.intercom_tags === 'string' ? JSON.parse(inputs.intercom_tags || '[]') : (inputs.intercom_tags || []);
const zendesk_tags = tags.map(t => t.name || t).concat(['intercom-sync']).join(',');

const subject = inputs.conversation_subject || 'Intercom #' + inputs.conversation_id;
const body_html = inputs.conversation_body || '';
const body_text = body_html.replace(/<[^>]*>/g, '').trim();

const external_id = 'intercom_' + inputs.conversation_id;
const zendesk_ticket = JSON.stringify({
  ticket: {
    subject: subject,
    comment: { body: body_text },
    priority: zendesk_priority,
    requester: { name: inputs.customer_name, email: inputs.customer_email },
    external_id: external_id,
    tags: zendesk_tags.split(',')
  }
});

return { zendesk_ticket, external_id, zendesk_priority, zendesk_tags, ticket_subject: subject, body_text, mapping_success: 'true' };`,
          inputVariables: [
            { name: "conversation_id", type: "string" },
            { name: "conversation_body", type: "string" },
            { name: "conversation_subject", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "intercom_tags", type: "string" },
            { name: "intercom_priority", type: "string" },
          ],
          outputVariables: [
            { name: "zendesk_ticket", type: "string" },
            { name: "external_id", type: "string" },
            { name: "zendesk_priority", type: "string" },
            { name: "zendesk_tags", type: "string" },
            { name: "ticket_subject", type: "string" },
            { name: "body_text", type: "string" },
            { name: "mapping_success", type: "string" },
          ],
        },
      }),

      // 4. Duplicate condition
      n("4", "condition", "Check: duplicate ticket exists?", 760, {
        conditionDetails: {
          conditions: [
            { field: "existing_ticket_id", operator: "notEmpty", value: "" },
          ],
        },
      }),

      // 5a. True: Update existing Zendesk ticket
      n("5", "hitApi", "Zendesk: Update existing ticket", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{existing_ticket_id}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value:
                '{"comment":{"body":"Updated from Intercom conversation #{conversation_id}:\\n{body_text}","public":false},"priority":"{zendesk_priority}"}',
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "zendesk_ticket_id",
              type: "string",
            },
          ],
        },
      }),

      // 5b. False: Create new Zendesk ticket
      n(
        "5b",
        "hitApi",
        "Zendesk: Create new ticket",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets.json",
            headers: [
              { key: "Authorization", value: "Basic {zendesk_auth}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "ticket",
                value:
                  '{"subject":"{ticket_subject}","comment":{"body":"{body_text}"},"priority":"{zendesk_priority}","requester":{"name":"{customer_name}","email":"{customer_email}"},"external_id":"{external_id}","tags":["{zendesk_tags}"]}',
              },
            ],
            variables: [
              {
                name: "ticket.id",
                customName: "zendesk_ticket_id",
                type: "string",
              },
              {
                name: "ticket.url",
                customName: "zendesk_ticket_url",
                type: "string",
              },
            ],
          },
        },
        500,
      ),

      // 6. Bi-directional sync: tag Intercom with Zendesk ID
      n("6", "hitApi", "Intercom: Tag with Zendesk ticket ID", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.intercom.io/conversations/{conversation_id}/tags",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "id", value: "zendesk-{zendesk_ticket_id}" }],
          variables: [
            { name: "id", customName: "sync_tag_id", type: "string" },
          ],
        },
      }),

      // 7. Exception Slack alert
      slackNode(
        "7",
        "Slack: Sync confirmation or exception",
        1480,
        "🔄 *Intercom → Zendesk Sync*\n*Intercom:* #{conversation_id} | *Zendesk:* #{zendesk_ticket_id}\n*Customer:* {customer_name} ({customer_email})\n*Subject:* {ticket_subject}\n*Priority:* {zendesk_priority} | *Tags:* {zendesk_tags}\n*Sync status:* Complete",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5b", "output_2"),
      e("5", "6"),
      e("5b", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 222. INTERCOM TO GOOGLE SHEET LOGGER (6 nodes)
  // ===================================================================

  "Intercom to Google Sheet Logger": {
    summary:
      "Receives Intercom webhook events, normalizes the data into a structured format, routes to the appropriate sheet tab based on event type, writes the record to Google Sheets, retries on failure, and alerts on anomalies via Slack.",
    nodes: [
      trigger("Webhook: Intercom event", "webhook_app", {
        source: "Intercom",
        event: "conversation.*",
        howItStarts:
          "Any Intercom conversation event fires (created, replied, closed, rated, tagged)",
        setup:
          "In Intercom -> Settings -> Webhooks, add a webhook for all conversation events pointing to the workflow URL. The workflow normalizes and logs every event to the appropriate Google Sheets tab.",
        inputFields: [],
      }),

      // 2. Normalization
      n("2", "runCode", "Normalize Intercom event data", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Normalize Intercom webhook into structured log entry
const raw = typeof inputs.webhook_payload === 'string' ? JSON.parse(inputs.webhook_payload) : inputs.webhook_payload;
const topic = raw.topic || 'unknown';
const item = raw.data?.item || {};
const conversation_id = item.id || '';
const customer_name = item.source?.author?.name || item.user?.name || '';
const customer_email = item.source?.author?.email || item.user?.email || '';
const subject = item.source?.subject || '';
const body = (item.source?.body || '').replace(/<[^>]*>/g, '').substring(0, 500);
const state = item.state || '';
const assignee = item.assignee?.name || 'Unassigned';
const rating = item.conversation_rating?.rating || '';
const tags = (item.tags?.tags || []).map(t => t.name).join(', ');
const created_at = item.created_at ? new Date(item.created_at * 1000).toISOString() : new Date().toISOString();

// Determine target tab based on topic
const tabMap = { 'conversation.created': 'NewConversations', 'conversation.user.replied': 'Replies', 'conversation.admin.replied': 'AdminReplies', 'conversation.closed': 'Closed', 'conversation.rated': 'Ratings' };
const target_tab = tabMap[topic] || 'Other';

return { topic, conversation_id, customer_name, customer_email, subject, body, state, assignee, rating, tags, created_at, target_tab, normalized: 'true' };`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "topic", type: "string" },
            { name: "conversation_id", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "subject", type: "string" },
            { name: "body", type: "string" },
            { name: "state", type: "string" },
            { name: "assignee", type: "string" },
            { name: "rating", type: "string" },
            { name: "tags", type: "string" },
            { name: "created_at", type: "string" },
            { name: "target_tab", type: "string" },
            { name: "normalized", type: "string" },
          ],
        },
      }),

      // 3. Tab routing — write to the correct tab
      n("3", "hitApi", "Write to routed Google Sheets tab", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/{target_tab}!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{created_at}","{topic}","{conversation_id}","{customer_name}","{customer_email}","{subject}","{body}","{state}","{assignee}","{rating}","{tags}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_written",
              type: "number",
            },
            {
              name: "updates.updatedRange",
              customName: "written_range",
              type: "string",
            },
          ],
        },
      }),

      // 4. Sheets write confirmation
      n("4", "hitApi", "Write to master log tab", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/MasterLog!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{created_at}","{topic}","{conversation_id}","{target_tab}","{rows_written}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "master_logged",
              type: "number",
            },
          ],
        },
      }),

      // 5. Retry condition
      n("5", "condition", "Write succeeded?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "rows_written", operator: "greaterThan", value: "0" },
          ],
        },
      }),

      // 6. False: Anomaly Slack alert (true branch continues silently)
      slackNode(
        "6",
        "Slack: Anomaly or failure alert",
        1240,
        "⚠️ *Intercom Logger Alert*\n*Event:* {topic} | *Conversation:* #{conversation_id}\n*Customer:* {customer_name} ({customer_email})\n*Tab:* {target_tab} | *Rows written:* {rows_written}\n*Status:* Potential write failure — please verify Sheets tab exists and credentials are valid.",
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_2"),
    ],
  },

  // ===================================================================
  // 223. INTERCOM TO WHATSAPP BRIDGE (8 nodes)
  // ===================================================================

  "Intercom to WhatsApp Bridge": {
    summary:
      "Bridges Intercom conversations and WhatsApp messages, resolves message direction, formats content for each platform, relays via Twilio WhatsApp API, syncs back to Intercom, handles media attachments, maintains conversation mapping, and logs errors for debugging.",
    nodes: [
      trigger("Webhook: Intercom or WhatsApp message", "webhook_app", {
        source: "Intercom / Twilio WhatsApp",
        event: "message.received",
        howItStarts:
          "A new message arrives from either Intercom (agent reply) or WhatsApp (customer message via Twilio) that needs to be bridged to the other platform",
        setup:
          "Configure both: 1) Intercom webhook for conversation.admin.replied events. 2) Twilio WhatsApp webhook for incoming messages. Both should POST to this workflow URL.",
        inputFields: [],
      }),

      // 2. Direction resolution
      n("2", "runCode", "Resolve message direction", 280, {
        codeDetails: {
          language: "javascript",
          code: `// Determine if message came from Intercom (agent) or WhatsApp (customer)
const raw = typeof inputs.webhook_payload === 'string' ? JSON.parse(inputs.webhook_payload) : inputs.webhook_payload;
let direction = 'unknown';
let message_text = '';
let sender_name = '';
let sender_phone = '';
let conversation_id = '';
let media_url = '';
let media_type = '';

// Intercom format (agent -> customer)
if (raw.topic && raw.topic.includes('intercom') || raw.data?.item) {
  direction = 'intercom_to_whatsapp';
  const item = raw.data?.item || {};
  message_text = (item.conversation_parts?.conversation_parts?.[0]?.body || '').replace(/<[^>]*>/g, '');
  sender_name = item.conversation_parts?.conversation_parts?.[0]?.author?.name || 'Agent';
  conversation_id = item.id || '';
}
// Twilio WhatsApp format (customer -> agent)
else if (raw.From && raw.From.includes('whatsapp')) {
  direction = 'whatsapp_to_intercom';
  message_text = raw.Body || '';
  sender_phone = raw.From.replace('whatsapp:', '');
  sender_name = raw.ProfileName || sender_phone;
  media_url = raw.MediaUrl0 || '';
  media_type = raw.MediaContentType0 || '';
  conversation_id = raw.WaId || '';
}

return { direction, message_text, sender_name, sender_phone, conversation_id, media_url, media_type, has_media: media_url ? 'true' : 'false' };`,
          inputVariables: [{ name: "webhook_payload", type: "string" }],
          outputVariables: [
            { name: "direction", type: "string" },
            { name: "message_text", type: "string" },
            { name: "sender_name", type: "string" },
            { name: "sender_phone", type: "string" },
            { name: "conversation_id", type: "string" },
            { name: "media_url", type: "string" },
            { name: "media_type", type: "string" },
            { name: "has_media", type: "string" },
          ],
        },
      }),

      // 3. Format for target platform
      n("3", "runCode", "Format message for target platform", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Format the message for the target platform
let formatted_text = inputs.message_text;
let target_platform = '';

if (inputs.direction === 'intercom_to_whatsapp') {
  target_platform = 'whatsapp';
  // Strip HTML, add agent prefix
  formatted_text = '[Agent ' + inputs.sender_name + '] ' + inputs.message_text;
} else if (inputs.direction === 'whatsapp_to_intercom') {
  target_platform = 'intercom';
  // Add WhatsApp prefix for agent visibility
  formatted_text = '[WhatsApp: ' + inputs.sender_name + '] ' + inputs.message_text;
  if (inputs.has_media === 'true') {
    formatted_text += '\\n[Attachment: ' + inputs.media_type + ' - ' + inputs.media_url + ']';
  }
}

return { formatted_text, target_platform, formatting_done: 'true' };`,
          inputVariables: [
            { name: "direction", type: "string" },
            { name: "message_text", type: "string" },
            { name: "sender_name", type: "string" },
            { name: "has_media", type: "string" },
            { name: "media_type", type: "string" },
            { name: "media_url", type: "string" },
          ],
          outputVariables: [
            { name: "formatted_text", type: "string" },
            { name: "target_platform", type: "string" },
            { name: "formatting_done", type: "string" },
          ],
        },
      }),

      // 4. WhatsApp relay via Twilio
      n("4", "whatsApp", "WhatsApp: Relay message via Twilio", 760, {
        whatsappDetails: {
          templateName: "bridge_relay",
          lang: "en",
          to: "{sender_phone}",
          smsBody: "{formatted_text}",
        },
      }),

      // 5. Intercom relay
      n("5", "hitApi", "Intercom: Relay message to conversation", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.intercom.io/conversations/{conversation_id}/reply",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message_type", value: "note" },
            { key: "type", value: "admin" },
            { key: "body", value: "{formatted_text}" },
          ],
          variables: [{ name: "id", customName: "reply_id", type: "string" }],
        },
      }),

      // 6. Media handling
      n("6", "runCode", "Handle media attachments", 1240, {
        codeDetails: {
          language: "javascript",
          code: `// Handle media transfer between platforms
let media_status = 'no_media';
let media_transfer_url = '';

if (inputs.has_media === 'true' && inputs.media_url) {
  // In production, download from source and upload to target platform
  media_status = 'pending_transfer';
  media_transfer_url = inputs.media_url;
} else {
  media_status = 'text_only';
}

return { media_status, media_transfer_url, media_handled: 'true' };`,
          inputVariables: [
            { name: "has_media", type: "string" },
            { name: "media_url", type: "string" },
          ],
          outputVariables: [
            { name: "media_status", type: "string" },
            { name: "media_transfer_url", type: "string" },
            { name: "media_handled", type: "string" },
          ],
        },
      }),

      // 7. Conversation mapping log
      n("7", "hitApi", "Log conversation mapping to Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/BridgeMapping!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{conversation_id}","{direction}","{sender_name}","{sender_phone}","{target_platform}","{media_status}","{has_media}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "mapping_logged",
              type: "number",
            },
          ],
        },
      }),

      // 8. Error logging
      slackNode(
        "8",
        "Slack: Bridge activity log",
        1720,
        "🔄 *Intercom ↔ WhatsApp Bridge*\n*Direction:* {direction}\n*From:* {sender_name} ({sender_phone})\n*Conversation:* #{conversation_id}\n*Media:* {media_status}\n*Message:* {formatted_text}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 224. LINEAR ISSUE TO SLACK THREAD (7 nodes)
  // ===================================================================

  "Linear Issue to Slack Thread": {
    summary:
      "Receives Linear issue webhooks, hydrates issue context, decides on the Slack action (new thread or reply), posts to Slack threads with full context, saves the Linear-to-Slack mapping, handles two-way sync conditions, and logs all sync activity.",
    nodes: [
      trigger("Webhook: Linear issue event", "webhook_app", {
        source: "Linear",
        event: "Issue",
        howItStarts:
          "A Linear issue is created, updated, or commented on, triggering a Slack thread sync",
        setup:
          "In Linear -> Settings -> API -> Webhooks, create a webhook for Issue events (created, updated) and Comment events. Point to the workflow URL.",
        inputFields: [],
      }),

      // 2. Context hydration
      aiNode(
        "2",
        "AI extract Linear issue context",
        280,
        "You are a project management assistant. Extract the Linear issue details from this webhook payload regardless of format. Find issue ID, title, description, state, priority, assignee, labels, team, and whether this is a create, update, or comment event. Respond JSON: {issue_id: string, issue_title: string, issue_description: string, issue_state: string, priority_label: string, assignee_name: string, labels: string, team_name: string, event_type: string, comment_body: string, issue_url: string}",
        "{webhook_payload}",
        [
          { field: "issue_id", as: "issue_id", type: "string" },
          { field: "issue_title", as: "issue_title", type: "string" },
          {
            field: "issue_description",
            as: "issue_description",
            type: "string",
          },
          { field: "issue_state", as: "issue_state", type: "string" },
          { field: "priority_label", as: "priority_label", type: "string" },
          { field: "assignee_name", as: "assignee_name", type: "string" },
          { field: "labels", as: "labels", type: "string" },
          { field: "team_name", as: "team_name", type: "string" },
          { field: "event_type", as: "event_type", type: "string" },
          { field: "comment_body", as: "comment_body", type: "string" },
          { field: "issue_url", as: "issue_url", type: "string" },
        ],
      ),

      // 3. Action decision
      n("3", "runCode", "Decide Slack action: new thread or reply", 520, {
        codeDetails: {
          language: "javascript",
          code: `// Decide whether to create a new Slack thread or reply to an existing one
const event = inputs.event_type;
let action = 'new_thread';
let thread_message = '';

if (event === 'created' || event === 'create') {
  action = 'new_thread';
  const priorityEmoji = { Urgent: '🔴', High: '🟠', Medium: '🟡', Low: '🟢' };
  const emoji = priorityEmoji[inputs.priority_label] || '⚪';
  thread_message = emoji + ' *New Linear Issue*\\n*' + inputs.issue_title + '*\\n' +
    'Priority: ' + inputs.priority_label + ' | State: ' + inputs.issue_state + '\\n' +
    'Assignee: ' + inputs.assignee_name + ' | Team: ' + inputs.team_name + '\\n' +
    'Labels: ' + inputs.labels + '\\n' +
    inputs.issue_description + '\\n' + inputs.issue_url;
} else if (event === 'comment' || event === 'commented') {
  action = 'reply';
  thread_message = '💬 *' + inputs.assignee_name + ':* ' + inputs.comment_body;
} else {
  action = 'reply';
  thread_message = '🔄 *Updated:* ' + inputs.issue_title + ' → ' + inputs.issue_state + ' (Assignee: ' + inputs.assignee_name + ')';
}

return { action, thread_message, is_new_thread: action === 'new_thread' ? 'true' : 'false' };`,
          inputVariables: [
            { name: "event_type", type: "string" },
            { name: "issue_title", type: "string" },
            { name: "issue_description", type: "string" },
            { name: "issue_state", type: "string" },
            { name: "priority_label", type: "string" },
            { name: "assignee_name", type: "string" },
            { name: "team_name", type: "string" },
            { name: "labels", type: "string" },
            { name: "issue_url", type: "string" },
            { name: "comment_body", type: "string" },
          ],
          outputVariables: [
            { name: "action", type: "string" },
            { name: "thread_message", type: "string" },
            { name: "is_new_thread", type: "string" },
          ],
        },
      }),

      // 4. Post to Slack thread
      slackNode("4", "Post to Slack thread", 760, "{thread_message}"),

      // 5. Save mapping
      n("5", "hitApi", "Save Linear-Slack thread mapping", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/LinearSlackMap!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{issue_id}","{issue_title}","{event_type}","{is_new_thread}","{priority_label}","{team_name}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "mapping_saved",
              type: "number",
            },
          ],
        },
      }),

      // 6. Two-way condition
      n("6", "condition", "Is this a new thread (needs two-way setup)?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "is_new_thread", operator: "equals", value: "true" },
          ],
        },
      }),

      // 7a. True: Setup two-way sync logging
      n("7", "hitApi", "Log two-way sync setup", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SyncLog!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{issue_id}","{issue_title}","two_way_active","{team_name}","{assignee_name}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "sync_logged",
              type: "number",
            },
          ],
        },
      }),

      // 7b. False: Simple sync log
      n(
        "7b",
        "hitApi",
        "Log one-way sync activity",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SyncLog!A1:append?valueInputOption=USER_ENTERED",
            headers: [
              { key: "Authorization", value: "Bearer {google_sheets_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{issue_id}","{event_type}","reply_synced","{team_name}","{assignee_name}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "reply_logged",
                type: "number",
              },
            ],
          },
        },
        500,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "7b", "output_2"),
    ],
  },

  // ===================================================================
  // 225. MICRO-INFLUENCER DM DRAFTER (7 nodes)
  // ===================================================================

  "Micro-Influencer DM Drafter": {
    summary:
      "Takes a list of potential micro-influencers, researches their recent content via SerpAPI, uses AI to score brand fit, drafts personalized DM outreach messages, gates on quality, queues approved drafts in Google Sheets, and notifies the partnership team via Slack.",
    nodes: [
      trigger("Webhook: Influencer list submitted", "webhook_app", {
        source: "Sheets / CRM / Manual",
        event: "influencer.list.submitted",
        howItStarts:
          "A list of potential micro-influencer handles/profiles is submitted for outreach planning",
        setup:
          "POST a list of influencer profiles (handles, platforms, follower counts, niche) to the workflow URL from your influencer CRM, Google Sheet trigger, or manual submission.",
        inputFields: [],
      }),

      // 2. Content research via SerpAPI
      aiNode(
        "2",
        "AI extract influencer details",
        280,
        "You are an influencer marketing assistant. Extract the influencer list from this payload regardless of field naming. Find influencer names, handles, platforms, niches, and any follower counts. Pick the first influencer to process. Respond JSON: {influencer_name: string, influencer_handle: string, platform: string, niche: string, follower_count: string, brand_name: string, campaign_goal: string, product_name: string}",
        "{webhook_payload}",
        [
          { field: "influencer_name", as: "influencer_name", type: "string" },
          {
            field: "influencer_handle",
            as: "influencer_handle",
            type: "string",
          },
          { field: "platform", as: "influencer_platform", type: "string" },
          { field: "niche", as: "influencer_niche", type: "string" },
          { field: "follower_count", as: "follower_count", type: "string" },
          { field: "brand_name", as: "brand_name", type: "string" },
          { field: "campaign_goal", as: "campaign_goal", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
        ],
      ),

      // 3. SerpAPI content research
      n("3", "hitApi", "SerpAPI: Research influencer content", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={influencer_handle}+{influencer_platform}+content&num=10&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "content_research",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI fit scoring + DM drafting
      aiNode(
        "4",
        "AI score fit and draft DM",
        760,
        "You are a micro-influencer outreach specialist. Based on the influencer profile and their recent content, score their brand fit (0-100) and draft a personalized DM. The DM must: reference specific recent content they created, explain the brand fit authentically, propose a collaboration that benefits both parties, feel personal (not templated). Respond JSON: {fit_score: number, fit_reasoning: string, dm_draft: string, collaboration_idea: string, content_referenced: string, tone: string, dm_quality: string}",
        "Influencer: {influencer_name} (@{influencer_handle}) on {influencer_platform}. Niche: {influencer_niche}. Followers: {follower_count}. Brand: {brand_name}. Product: {product_name}. Campaign goal: {campaign_goal}. Recent content: {content_research}",
        [
          { field: "fit_score", as: "fit_score", type: "number" },
          { field: "fit_reasoning", as: "fit_reasoning", type: "string" },
          { field: "dm_draft", as: "dm_draft", type: "string" },
          {
            field: "collaboration_idea",
            as: "collaboration_idea",
            type: "string",
          },
          {
            field: "content_referenced",
            as: "content_referenced",
            type: "string",
          },
          { field: "tone", as: "dm_tone", type: "string" },
          { field: "dm_quality", as: "dm_quality", type: "string" },
        ],
      ),

      // 5. Quality condition
      n("5", "condition", "Fit score above threshold?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "fit_score", operator: "greaterThan", value: "60" },
          ],
        },
      }),

      // 6a. True: Queue in Sheets
      n("6", "hitApi", "Queue approved DM to Sheets", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/DMQueue!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_sheets_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{influencer_name}","{influencer_handle}","{influencer_platform}","{fit_score}","{dm_draft}","{collaboration_idea}","{content_referenced}","pending"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "dm_queued",
              type: "number",
            },
          ],
        },
      }),

      // 6b. False: Low fit — skip with log
      n(
        "6b",
        "hitApi",
        "Log low-fit influencer to Sheets",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/Skipped!A1:append?valueInputOption=USER_ENTERED",
            headers: [
              { key: "Authorization", value: "Bearer {google_sheets_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{influencer_name}","{influencer_handle}","{fit_score}","{fit_reasoning}","skipped"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "skip_logged",
                type: "number",
              },
            ],
          },
        },
        500,
      ),

      // 7. Slack notification
      slackNode(
        "7",
        "Slack: Notify partnerships team",
        1480,
        "🤝 *Influencer DM Drafted*\n*Influencer:* {influencer_name} (@{influencer_handle}) on {influencer_platform}\n*Followers:* {follower_count} | *Niche:* {influencer_niche}\n*Fit Score:* {fit_score}/100\n*Reasoning:* {fit_reasoning}\n*Collab Idea:* {collaboration_idea}\n*DM Draft:*\n{dm_draft}\n*Content Referenced:* {content_referenced}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "6b", "output_2"),
      e("6", "7"),
      e("6b", "7"),
    ],
  },

  // ===================================================================
  // 226. MICROSOFT TEAMS MEETING SUMMARIZER (7 nodes)
  // ===================================================================

  "Microsoft Teams Meeting Summarizer": {
    summary:
      "Triggers when a Teams meeting ends, retrieves the transcript via Microsoft Graph API, analyzes the discussion with AI, generates a structured summary with action items, distributes via email, posts to the Teams channel, and creates Asana tasks for each action item.",
    nodes: [
      trigger("Webhook: Teams meeting ended", "webhook_app", {
        source: "Microsoft Teams / Graph API",
        event: "callRecords/callEnded",
        howItStarts:
          "A Microsoft Teams meeting ends and has a transcript available for summarization",
        setup:
          "Register an app in Azure AD with CallRecords.Read.All and OnlineMeetings.Read.All permissions. Set up a Graph API subscription for callRecords events pointing to the workflow URL.",
        inputFields: [],
      }),

      // 2. Fetch transcript via Microsoft Graph
      n("2", "hitApi", "Microsoft Graph: Fetch meeting transcript", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.microsoft.com/v1.0/me/onlineMeetings/{meeting_id}/transcripts/{transcript_id}/content",
          headers: [
            { key: "Authorization", value: "Bearer {microsoft_graph_token}" },
            { key: "Accept", value: "text/vtt" },
          ],
          body: [],
          variables: [
            { name: "content", customName: "raw_transcript", type: "string" },
          ],
        },
      }),

      // 3. AI analysis
      aiNode(
        "3",
        "AI analyze meeting transcript",
        520,
        "You are a meeting analyst. Analyze this Microsoft Teams meeting transcript. Extract: key discussion topics, decisions made, action items with owners and deadlines, open questions, parking lot items, and meeting sentiment. Respond JSON: {meeting_title: string, duration_minutes: number, participants: string[], key_topics: string[], decisions: string[], action_items: string[], action_owners: string[], action_deadlines: string[], open_questions: string[], parking_lot: string[], overall_sentiment: string, follow_up_needed: string}",
        "Meeting transcript: {raw_transcript}",
        [
          { field: "meeting_title", as: "meeting_title", type: "string" },
          { field: "duration_minutes", as: "duration_minutes", type: "number" },
          { field: "participants", as: "participants", type: "string" },
          { field: "key_topics", as: "key_topics", type: "string" },
          { field: "decisions", as: "decisions", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "action_owners", as: "action_owners", type: "string" },
          { field: "action_deadlines", as: "action_deadlines", type: "string" },
          { field: "open_questions", as: "open_questions", type: "string" },
          { field: "parking_lot", as: "parking_lot", type: "string" },
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          { field: "follow_up_needed", as: "follow_up_needed", type: "string" },
        ],
      ),

      // 4. AI summary generation
      aiNode(
        "4",
        "AI generate structured summary",
        760,
        "You are an executive communications specialist. Transform this meeting analysis into a clean, scannable summary document. Use headers, bullet points, and bold for key items. Include an executive TL;DR at the top. Format action items as a checklist with owners and deadlines. Respond JSON: {executive_tldr: string, formatted_summary: string, action_checklist: string, email_body_html: string, teams_post_text: string}",
        "Title: {meeting_title}. Duration: {duration_minutes}min. Participants: {participants}. Topics: {key_topics}. Decisions: {decisions}. Actions: {action_items}. Owners: {action_owners}. Deadlines: {action_deadlines}. Open: {open_questions}. Parking lot: {parking_lot}. Sentiment: {overall_sentiment}",
        [
          { field: "executive_tldr", as: "executive_tldr", type: "string" },
          {
            field: "formatted_summary",
            as: "formatted_summary",
            type: "string",
          },
          { field: "action_checklist", as: "action_checklist", type: "string" },
          { field: "email_body_html", as: "email_body_html", type: "string" },
          { field: "teams_post_text", as: "teams_post_text", type: "string" },
        ],
      ),

      // 5. Email distribution
      emailNode(
        "5",
        "Email meeting summary to attendees",
        1000,
        "{meeting_attendee_emails}",
        "Meeting Summary: {meeting_title} ({duration_minutes}min)",
        "{email_body_html}",
      ),

      // 6. Teams channel posting
      n("6", "hitApi", "Post summary to Teams channel", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://graph.microsoft.com/v1.0/teams/{team_id}/channels/{channel_id}/messages",
          headers: [
            { key: "Authorization", value: "Bearer {microsoft_graph_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "body",
              value: '{"contentType":"html","content":"{teams_post_text}"}',
            },
          ],
          variables: [
            { name: "id", customName: "teams_message_id", type: "string" },
          ],
        },
      }),

      // 7. Create Asana tasks for action items
      n("7", "hitApi", "Asana: Create action item tasks", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"[Meeting] {meeting_title}: Action Items","notes":"{action_checklist}\\n\\nFrom meeting: {meeting_title}\\nDecisions: {decisions}\\nOpen questions: {open_questions}","projects":["{asana_project_id}"],"assignee":"me"}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
